/**
 * Authored Web Lab lessons — the page the student edits, the checks that grade it,
 * and the instructions that ask for the change, written together as ONE set.
 *
 * Keyed by lessonID, so this one file serves both CD02 (V2###) and CD03 (V3###).
 *
 * WHY ALL THREE LIVE HERE
 *
 * The generator (backend/src/services/ClaudeCourseService.js) produces a coherent
 * triple for a `grading: "dom"` lesson — `starterCode`, `domTests` and the prose —
 * and is explicitly told to write the exercise "so that each of the domTests
 * descriptions clearly follows from it". The hub then replaces the starter and the
 * checks with the authored pair below. For a long time it did NOT replace the prose,
 * so the instructions on screen described the page the generator had imagined and
 * then discarded: V3303 asked the student to restyle `.card` and a button on a page
 * whose only elements are `#titulo` and `.destacado`.
 *
 * A scaffold, its checks and its instructions are one artifact. Splitting them
 * across a data file and a model is what produced that bug (the same way the
 * generator once invented `#jokeBtn`/`#joke` for V2407 while the scaffold had
 * `#preguntar`/`#respuesta`), so all three are authored here and all three override
 * whatever was generated. `lessonMarkdown` is the one piece still generated: it
 * teaches the concept rather than the page, and it is narrated by pregenerated
 * ElevenLabs audio keyed per lesson+locale, which rewriting it would desync.
 *
 * TWO KINDS OF SCAFFOLD, PLUS THE MODULE 6 SCREENS
 *
 * 1. The Module 4 BUILD CHAIN (both courses). Every Module 4 Web Lab lesson has to
 *    START from the previous lesson's result, otherwise "your app" is really a pile
 *    of unrelated exercises. Leaving that to the lesson generator doesn't work: it
 *    would invent a different app on every regeneration and the thread from day one
 *    would break. Step 0 is the branded template from V2102 / V3102, so the app
 *    carries the name and colours the student chose on day one all the way to
 *    publishing in Module 5.
 *
 * 2. CD03's Module 3 READING PAGES. That module teaches recognising and placing
 *    code rather than writing it, so each page already works and asks for exactly
 *    one small change. They use `miniPage` rather than the full `shell`: a beginner
 *    meeting code for the first time has to be able to read the WHOLE file, and 90
 *    lines of branded CSS defeats that before the lesson starts.
 *
 * Neither of those is CD02's Module 6. Its seven REPLICAS of the Claude Code desktop
 * window are not scaffolds at all — nothing about them is edited. They live in
 * AUTHORED_SCREENS / AUTHORED_HOTSPOTS near the bottom of this file, arrive finished,
 * and are graded by clicking rather than by typing; the long comment above them
 * explains why they stopped being Web Lab lessons. They share `bilingual`, `COPY` and
 * the `claudeCodeScreen` chrome with everything else here, which is why they have not
 * moved to a file of their own.
 *
 * Each document — scaffold or screen — is COMPLETE and self-contained, with no network
 * requests of any kind, because both previews run in a sandboxed iframe with no
 * network (see webLabHarness.js and hotspotHarness.js). Visuals are emoji or CSS only.
 *
 * `{{APP_NAME}}`, `{{TAGLINE}}`, `{{ACCENT}}` and `{{EMOJI}}` are filled from the
 * student's `users_projects` record by `buildAuthoredStarter` below.
 *
 * LOCALIZATION
 *
 * Everything student-facing exists in both `es` and `en`. All of it comes from a
 * single per-lesson entry in `COPY`, which the scaffold, the checks and the exercise
 * text all read from — so a phrase a check asserts on is literally the same string
 * the scaffold prints, in either language, and cannot drift.
 *
 * THE ONE RULE: ids, class names and localStorage keys are IDENTICAL across locales.
 * Only comments, visible copy and the `lang` attribute translate. That is what lets
 * one set of check selectors grade both languages, and it is why an English scaffold
 * still says `#titulo` and `.destacado` — those are the names this page gave its
 * elements, not words to be translated. `authoredLessons.test.js` enforces it.
 */

import { DEFAULT_BRAND } from "./starterAppHtml.js";

/**
 * Build the `es` and `en` variants of one thing from a single definition.
 *
 * `build` receives that locale's copy and the locale tag, and is called once per
 * language — so the HTML structure, the selectors and the check shapes are written
 * ONCE and only the words differ.
 */
const bilingual = (copy, build) => ({
    es: build(copy.es, "es"),
    en: build(copy.en, "en"),
});

/**
 * The three part labels repeated on EVERY page of CD03's Module 3.
 *
 * Same words, same order, every lesson: by the time the student reaches V3305 ("how
 * the three fit together") the mental model has been built by repetition rather than
 * explanation. Translating them means translating all three together.
 */
const PART_LABELS = {
    es: {
        structure: "PARTE 1 — LA ESTRUCTURA (HTML): lo que se ve en la página",
        style: "PARTE 2 — EL ESTILO (CSS): cómo se ve todo esto",
        behavior: "PARTE 3 — EL COMPORTAMIENTO (JavaScript): lo que pasa cuando tocas algo",
    },
    en: {
        structure: "PART 1 — THE STRUCTURE (HTML): what you see on the page",
        style: "PART 2 — THE STYLE (CSS): how all of it looks",
        behavior: "PART 3 — THE BEHAVIOR (JavaScript): what happens when you tap something",
    },
};

const shell = (bodyInner, extraStyle = "", extraScript = "", lang = "es") => `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>{{APP_NAME}}</title>
<style>
  :root { --accent: {{ACCENT}}; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: #f6f7fb;
    color: #1f2333;
  }
  header {
    background: var(--accent);
    color: #fff;
    padding: 16px 18px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  header .logo { font-size: 24px; line-height: 1; }
  header h1 { font-size: 18px; margin: 0; font-weight: 700; }
  header p { margin: 2px 0 0; font-size: 12px; opacity: 0.9; }
  main { padding: 16px; }
  .card {
    background: #fff;
    border: 1px solid #e6e8f0;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .card h2 { margin: 0 0 8px; font-size: 16px; }
  .card p { margin: 0; font-size: 14px; color: #6b7189; line-height: 1.5; }
  .btn {
    background: var(--accent); color: #fff;
    border: 0; border-radius: 8px; padding: 10px 16px;
    font-size: 14px; font-weight: 700; cursor: pointer;
  }
  input, textarea {
    width: 100%; padding: 10px 12px; font-size: 14px;
    border: 1px solid #e6e8f0; border-radius: 8px; margin-bottom: 10px;
  }
  ul { list-style: none; margin: 12px 0 0; padding: 0; }
  li {
    display: flex; align-items: center; gap: 9px;
    padding: 10px 0; border-bottom: 1px solid #f0f1f6; font-size: 14px;
  }
  li:last-child { border-bottom: 0; }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); flex: 0 0 auto; }
${extraStyle}</style>
</head>
<body>
  <header>
    <span class="logo">{{EMOJI}}</span>
    <div>
      <h1>{{APP_NAME}}</h1>
      <p>{{TAGLINE}}</p>
    </div>
  </header>

  <main>
${bodyInner}
  </main>
${extraScript}</body>
</html>`;

/** The Module 3 page for CD03 — small enough to read end to end. */
const miniPage = ({ style = "", body = "", script = "" }, lang = "es") => {
    const parts = PART_LABELS[lang] || PART_LABELS.es;
    return `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>{{APP_NAME}}</title>

<!-- ${parts.style} -->
<style>
  body {
    font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
    background: #f6f7fb; color: #1f2333;
    margin: 0; padding: 24px; line-height: 1.5;
  }
${style}</style>
</head>
<body>

<!-- ${parts.structure} -->
${body}
<!-- ${parts.behavior} -->
<script>
${script}</script>

</body>
</html>`;
};

/**
 * The shared chrome for CD02's replica screens — Module 6's seven views of the
 * Claude desktop app, plus V2501's version-history panel in Module 5.
 *
 * It is a plain dark application window, which is why V2501 can borrow it without
 * pretending to be Claude Code: its screen is a generic version history rather than
 * the real app, so it teaches the shape that GitHub Desktop, VS Code and Claude
 * Code's own project bar all share. Module 5's other replica, V2512, is a web page
 * rather than an app and uses `browserScreen` below instead. Everything from here on
 * describes the Module 6 replicas specifically.
 *
 * Module 6 teaches the desktop app, not the terminal (see V2601/V2602: a course
 * whose students have never opened a terminal should not start there). So these
 * replicas are the desktop window and its menus — NOT a terminal, which is what an
 * early draft of this module wrongly showed.
 *
 * ONE REPLICA PER CONTROL. A single screenshot-sized mock of the whole window can
 * only be looked at; it cannot teach "how do I change the model", because changing
 * the model is a menu you open. So each lesson gets the one piece of UI it is
 * about, and the student operates it:
 *
 *   V2603  the Chat / Cowork / Code tab bar
 *   V2604  the environment chooser and Select folder
 *   V2605  the whole session window (the anatomy lesson)
 *   V2606  the model menu
 *   V2607  the effort menu
 *   V2608  the permission mode selector
 *   V2609  a proposed change, with its diff and its two buttons
 *
 * Everything is inert: every control is wired to local JavaScript and nothing here
 * talks to anything. The region comments are the lesson — to find the two places
 * they type, the student has to read the whole thing.
 *
 * NOT built on `miniPage` or `shell`: both carry Module 3's PART_LABELS scaffolding
 * and a light app-shaped theme, neither of which belongs on this window.
 *
 * UPKEEP: a mock drifts. When the real app moves, this is the file to fix, along
 * with the prose in backend/src/data/claudeCodeLessonProse.js — the two name each
 * other. Keep them approximate on purpose: they teach the REGIONS, the CONTROLS and
 * what they are for, not pixel placement.
 */
const claudeCodeScreen = ({ head = "", body = "", script = "" }, lang = "es") => `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Claude Code</title>
<style>
  body {
    margin: 0; padding: 12px;
    background: #14130f;
    font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
    font-size: 13px; line-height: 1.5; color: #e5e3de;
  }
  .app {
    display: flex; max-width: 720px; margin: 0 auto;
    border: 1px solid #3a3833; border-radius: 10px; overflow: hidden;
    background: #262624; min-height: 420px;
  }

  /* left column — sessions */
  .side { flex: 0 0 158px; background: #191918; border-right: 1px solid #3a3833; padding: 8px; }
  .iconrow { display: flex; gap: 9px; padding: 2px 4px 8px; color: #6f6c66; font-size: 12px; }
  .tabs { display: flex; gap: 4px; margin-bottom: 10px; }
  .tab { flex: 1; text-align: center; padding: 4px; border-radius: 6px; font-size: 11px; color: #8a8782; }
  .tab.on { background: #333331; color: #e5e3de; }
  .side-item { padding: 4px 6px; color: #b8b5ae; font-size: 12px; }
  .side-h { margin: 12px 0 4px; padding: 0 6px; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #6f6c66; }
  .chat { padding: 5px 6px; border-radius: 5px; font-size: 11px; color: #a8a49c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .chat.on { background: #333331; color: #e5e3de; }
  .me { margin-top: 12px; padding: 6px; border-top: 1px solid #3a3833; font-size: 11px; color: #8a8782; }

  /* right column — the conversation */
  .main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
  .topbar { padding: 9px 12px; border-bottom: 1px solid #3a3833; display: flex; align-items: center; gap: 7px; }
  .badge { background: #333331; color: #b8b5ae; border-radius: 5px; padding: 1px 7px; font-size: 11px; }
  .thread { flex: 1; padding: 12px; }
  .dim { color: #8a8782; font-size: 12px; }
  .act { border-left: 2px solid #4a4740; padding-left: 10px; margin-bottom: 12px; color: #a8a49c; font-size: 12px; }
  .ask { background: #1f1e1d; border: 1px solid #4a4740; border-radius: 8px; padding: 11px; margin-bottom: 12px; }
  .ask-t { margin-bottom: 7px; }
  .diff { background: #14130f; border-radius: 6px; padding: 9px; margin: 0 0 9px; font-size: 12px;
          font-family: ui-monospace, Menlo, Consolas, monospace; white-space: pre-wrap; }
  .add { color: #6ee7a0; }
  .btn { font: inherit; font-size: 12px; border: 1px solid #4a4740; border-radius: 6px; padding: 5px 12px; cursor: pointer; background: #333331; color: #e5e3de; }
  .yes { background: #d97757; border-color: #d97757; color: #14130f; font-weight: 600; }
  .btn[disabled] { opacity: .4; cursor: default; }

  /* bottom — where you type */
  .composer { border-top: 1px solid #3a3833; padding: 9px 12px 11px; }
  .projbar { display: flex; align-items: center; gap: 7px; font-size: 11px; color: #8a8782;
             background: #1f1e1d; border: 1px solid #3a3833; border-radius: 7px; padding: 6px 9px; margin-bottom: 7px; }
  .branch { background: #333331; border-radius: 4px; padding: 1px 6px; color: #b8b5ae; }
  .stat { margin-left: auto; }
  .plus { color: #6ee7a0; }
  .minus { color: #e08a7a; }
  .pr { font: inherit; font-size: 11px; background: #333331; color: #e5e3de; border: 1px solid #4a4740; border-radius: 5px; padding: 2px 8px; cursor: pointer; }
  .box { background: #1f1e1d; border: 1px solid #4a4740; border-radius: 7px; padding: 9px 10px; min-height: 20px; color: #e5e3de; }
  .ctrls { display: flex; align-items: center; gap: 6px; margin-top: 7px; }
  .pill { background: #333331; border-radius: 5px; padding: 2px 8px; font-size: 11px; color: #b8b5ae; }
  .pill.mode { background: #d97757; color: #14130f; font-weight: 600; }
  .grow { margin-left: auto; }

  /* menus, tab bars and pickers — the lessons that teach ONE control each */
  .app.col { flex-direction: column; min-height: 0; }
  .pad { padding: 12px; }
  .h { margin: 13px 0 5px; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #6f6c66; }
  .tabbar { display: flex; gap: 6px; justify-content: center; padding: 9px; border-bottom: 1px solid #3a3833; }
  .tabbtn { font: inherit; font-size: 12px; background: #1f1e1d; color: #b8b5ae; border: 1px solid #3a3833;
            border-radius: 6px; padding: 4px 14px; cursor: pointer; min-width: 78px; }
  .tabbtn.on { background: #333331; color: #e5e3de; }
  .menu { background: #1f1e1d; border: 1px solid #4a4740; border-radius: 8px; padding: 6px; margin: 9px 0; }
  .mrow { display: flex; gap: 10px; align-items: baseline; padding: 6px 8px; border-radius: 6px; }
  .mrow.on { background: #333331; }
  .mkey { flex: 0 0 132px; color: #e5e3de; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; }
  .mdesc { color: #8a8782; font-size: 12px; }
  /* An empty span the student fills in. Dashed so a blank is visible on screen.
     These are :not(:empty) check targets, so they MUST ship with nothing inside. */
  .blank { display: inline-block; min-width: 96px; border-bottom: 1px dashed #6f6c66; color: #e5e3de; }
  .kbd { background: #333331; border: 1px solid #4a4740; border-radius: 4px; padding: 1px 6px;
         font-size: 11px; color: #b8b5ae; font-family: ui-monospace, Menlo, Consolas, monospace; }
  .warn { color: #e08a7a; font-size: 12px; margin: 9px 0; }
  .row { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; margin: 8px 0; }
${head}</style>
</head>
<body>

${body}${script ? `<script>
${script}</script>
` : ""}
</body>
</html>`;

/**
 * The second chrome: a LIGHT browser window with an address bar, for V2512.
 *
 * `claudeCodeScreen` above is a dark application window, and V2512 is not an
 * application — it is a web page on somebody else's site. The address bar is not
 * decoration either: half that lesson is the difference between where your code
 * lives (`github.com/you/your-app`) and where your app runs
 * (`you.github.io/your-app`), and you cannot ask that question without showing the
 * bar the answer is typed into.
 *
 * Deliberately approximate. It teaches the REGIONS of a repository page — address,
 * tab strip, branch, commit count, last-commit line, file list — not GitHub's
 * current pixels, which is the only way a hand-built replica survives a redesign.
 * When GitHub moves something that matters, this and the V2511–V2514 prose in
 * backend/src/data/authoredLessonProse.js are the pair to fix; they name each other.
 *
 * No `href` anywhere on purpose: the screen runs in a sandboxed iframe with no
 * network, and authoredLessons.test.js fails any `http:` src or href, so links are
 * spans and buttons that look like links and go nowhere.
 */
const browserScreen = ({ head = "", body = "" }, lang = "es") => `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>GitHub</title>
<style>
  body {
    margin: 0; padding: 12px;
    background: #eef0f3;
    font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
    font-size: 13px; line-height: 1.5; color: #1f2328;
  }
  .win {
    max-width: 720px; margin: 0 auto;
    border: 1px solid #c9cdd3; border-radius: 10px; overflow: hidden;
    background: #ffffff;
  }

  /* the browser itself */
  .chrome { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #e3e6ea; border-bottom: 1px solid #c9cdd3; }
  .dots { color: #9aa0a6; letter-spacing: 2px; font-size: 11px; }
  .url { flex: 1; background: #ffffff; border: 1px solid #c9cdd3; border-radius: 14px;
         padding: 4px 12px; font-size: 12px; color: #3d4450;
         font-family: ui-monospace, Menlo, Consolas, monospace;
         white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .avatar { flex: 0 0 auto; width: 22px; height: 22px; border-radius: 50%;
            background: #d0d7de; text-align: center; line-height: 22px; font-size: 11px; }

  /* the page */
  .page { padding: 12px 14px 16px; }
  .repohead { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .owner { color: #636c76; font-size: 15px; }
  .reponame { font-size: 15px; font-weight: 600; color: #0969da; }
  .vis { border: 1px solid #d0d7de; border-radius: 12px; padding: 0 8px; font-size: 11px; color: #636c76; }
  .navtabs { display: flex; gap: 4px; margin: 11px 0 0; border-bottom: 1px solid #d0d7de; }
  .navtab { font: inherit; font-size: 12px; background: none; border: none; border-bottom: 2px solid transparent;
            padding: 6px 11px; color: #1f2328; cursor: pointer; }
  .navtab.on { font-weight: 600; border-bottom-color: #fd8c73; }

  .toolbar { display: flex; align-items: center; gap: 8px; margin: 13px 0 9px; }
  .branch { border: 1px solid #d0d7de; border-radius: 6px; background: #f6f8fa;
            padding: 3px 10px; font-size: 12px; color: #1f2328; }
  .commits { margin-left: auto; font-size: 12px; color: #636c76; }
  .commits b { color: #1f2328; }

  .list { border: 1px solid #d0d7de; border-radius: 6px; overflow: hidden; }
  .commitline { display: flex; align-items: center; gap: 8px; background: #f6f8fa;
                border-bottom: 1px solid #d0d7de; padding: 7px 12px; font-size: 12px; }
  .who { font-weight: 600; }
  .msg { color: #1f2328; }
  .when { margin-left: auto; color: #636c76; }
  .file { display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #eaeef2; padding: 6px 12px; font-size: 12px; }
  .file:last-child { border-bottom: none; }
  .fname { color: #0969da; }
  .fmsg { color: #636c76; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fwhen { margin-left: auto; color: #636c76; white-space: nowrap; }
  .readme { border: 1px solid #d0d7de; border-radius: 6px; margin-top: 13px; padding: 11px 13px; }
  .readme h2 { margin: 0 0 5px; font-size: 15px; }
  .readme p { margin: 0; color: #636c76; font-size: 12px; }
${head}</style>
</head>
<body>

${body}
</body>
</html>`;

/**
 * Everything student-facing, per lesson, per language.
 *
 * `checks` holds the check descriptions; `exercise` and `hint` become the lesson's
 * `exercisePrompt` / `hint`. Words a check ASSERTS on (V2307's greetings, V2407's
 * echo phrase) live here too, so the scaffold prints exactly what the check looks
 * for in both languages.
 *
 * Every `exercise` is written FROM that lesson's checks: each check description has
 * to read as an obvious consequence of it. That is the contract the generator was
 * given and could not keep, because it never saw the page the student receives.
 */
const COPY = {
    // ── CD02 Module 5 ────────────────────────────────────────────────────────
    // V2501 — Git. The only screen outside Module 6, and it borrows Module 6's
    // model for the same reason those lessons did: Git is a state machine, and the
    // rubric-graded chat this lesson used to have could not show state. Its criteria
    // all began "Student asked Claude to…", so four typed questions passed it
    // whether or not the student understood anything.
    //
    // The screen is deliberately NOT the Claude Code window: V2501 comes before
    // Module 6, so a student meeting that window here would be meeting it twice and
    // out of order. It is a generic version-history panel — the shape GitHub
    // Desktop, VS Code and Claude Code's own project bar all share — so the branch
    // chip and the +/− stat are already familiar by the time Module 6 arrives.
    V2501: {
        es: {
            title: "Historial de versiones",
            pendingHead: "ahora mismo",
            pending: "3 archivos con cambios sin guardar",
            saveHead: "guardar una versión",
            msgPlaceholder: "Agregué el botón de borrar a la lista",
            commit: "Commit",
            branch: "main",
            push: "Subir a GitHub",
            historyHead: "versiones guardadas",
            h3: "a1b2c3",
            d3: "Agregué el botón de borrar a la lista",
            t3: "hace 2 minutos",
            h2: "9f4e21",
            d2: "Los precios ahora se guardan al recargar",
            t2: "ayer",
            h1: "7c0d55",
            d1: "Primera versión de mi app",
            t1: "hace 10 días",
            restore: "Restore",
            stat: "+12 −1",
            spots: {
                pending: {
                    prompt: "Tu computadora se muere en este instante. Haz clic en lo que te dice exactamente cuánto trabajo perderías.",
                    label: "Todavía no está en el historial — esto es lo que se pierde",
                    miss: "Eso ya está guardado. Busca lo que todavía NO lo está.",
                },
                commit: {
                    prompt: "El botón de borrar por fin funciona. Haz clic en lo que presionas para poder volver a este momento exacto siempre que quieras.",
                    label: "Guarda una versión a la que puedes volver",
                },
                message: {
                    prompt: "Es dentro de seis meses y estás buscando una versión. Haz clic en lo único de esa fila que te dice qué cambió.",
                    label: "La nota para tu yo del futuro",
                    miss: "Eso identifica la versión, pero no te dice qué tiene dentro.",
                },
                restore: {
                    prompt: "Pasaste la mañana empeorando la página. Haz clic en lo que te devuelve la versión de ayer.",
                    label: "Tus archivos vuelven a esa versión",
                },
                branch: {
                    prompt: "Quieres probar un diseño completamente distinto sin arriesgar la versión que ya funciona. Haz clic en lo que te deja hacerlo aparte.",
                    label: "Una línea de trabajo aparte",
                },
                push: {
                    prompt: "Esta noche te roban la computadora. Haz clic en lo que hace que mañana tu app siga existiendo.",
                    label: "La copia en línea — tu respaldo",
                },
            },
            exercise: `Esta es una pantalla de historial de versiones. Responde las seis preguntas de arriba haciendo clic en la parte de la pantalla que cada una describe.`,
            hint: "Aquí no se escribe nada. En cada pregunta piensa qué necesitas: guardar algo, encontrar algo, volver atrás, o tener una copia en otro lado.",
        },
        en: {
            title: "Version history",
            pendingHead: "right now",
            pending: "3 files with unsaved changes",
            saveHead: "save a version",
            msgPlaceholder: "Added the delete button to the list",
            commit: "Commit",
            branch: "main",
            push: "Push to GitHub",
            historyHead: "saved versions",
            h3: "a1b2c3",
            d3: "Added the delete button to the list",
            t3: "2 minutes ago",
            h2: "9f4e21",
            d2: "Prices now survive a reload",
            t2: "yesterday",
            h1: "7c0d55",
            d1: "First version of my app",
            t1: "10 days ago",
            restore: "Restore",
            stat: "+12 −1",
            spots: {
                pending: {
                    prompt: "Your computer dies this second. Click the thing that tells you exactly how much work you would lose.",
                    label: "Not in the history yet — this is what goes",
                    miss: "That one is already saved. Look for what still isn't.",
                },
                commit: {
                    prompt: "The delete button finally works. Click what you press to make this exact moment something you can always come back to.",
                    label: "Saves a version you can return to",
                },
                message: {
                    prompt: "It is six months from now and you are hunting for a version. Click the only thing on that row that tells you what changed.",
                    label: "The note to your future self",
                    miss: "That identifies the version, but it doesn't tell you what is in it.",
                },
                restore: {
                    prompt: "You spent all morning making the page worse. Click what gives you yesterday's version back.",
                    label: "Your files go back to that version",
                },
                branch: {
                    prompt: "You want to try a completely different design without risking the version that already works. Click what lets you do it separately.",
                    label: "A separate line of work",
                },
                push: {
                    prompt: "Your computer is stolen tonight. Click the thing that means your app still exists tomorrow.",
                    label: "The online copy — your backup",
                },
            },
            exercise: `This is a version history screen. Answer the six questions above by clicking the part of the screen each one describes.`,
            hint: "Nothing gets typed here. For each question, think about what you need: to save something, to find something, to go back, or to have a copy somewhere else.",
        },
    },

    // V4511 — CD04's version-history screen. Same panel as V2501 above, minus GitHub.
    //
    // WHY A SEPARATE COPY INSTEAD OF AN ALIAS. CD04 ships Module 5 with Firebase
    // Hosting, so its students never push anything anywhere. V2501's sixth question —
    // "your computer is stolen tonight, click the thing that means your app still
    // exists tomorrow" — is GRADED, and in CD04 its answer is a button for a thing the
    // course never teaches. So this drops `push` and `spots.push`, and the screen below
    // drops the button, leaving five questions about LOCAL history: what am I about to
    // lose, how do I save it, how do I find it again, how do I get it back, and how do
    // I work on something risky separately.
    //
    // V2501 keeps its six. CD02 still ships GitHub Pages and the question is correct
    // there — editing it in place would have quietly broken a live course to fix this
    // one. Same reasoning as PROJECT_SETUP_V4 in pages/claudeDevCourse/authoredChecklists.js.
    //
    // WHAT CD04 GIVES UP by dropping it, stated so nobody re-adds the button without
    // meaning to: CD04 has NO offsite backup story. Local commits survive a bad edit,
    // not a stolen laptop. V4511's prose says so plainly rather than implying otherwise
    // — see backend/src/data/authoredLessonProse.js.
    V4511: {
        es: {
            title: "Historial de versiones",
            pendingHead: "ahora mismo",
            pending: "3 archivos con cambios sin guardar",
            saveHead: "guardar una versión",
            msgPlaceholder: "Agregué el botón de borrar a la lista",
            commit: "Commit",
            branch: "main",
            historyHead: "versiones guardadas",
            h3: "a1b2c3",
            d3: "Agregué el botón de borrar a la lista",
            t3: "hace 2 minutos",
            h2: "9f4e21",
            d2: "Los precios ahora se guardan al recargar",
            t2: "ayer",
            h1: "7c0d55",
            d1: "Primera versión de mi app",
            t1: "hace 10 días",
            restore: "Restore",
            stat: "+12 −1",
            spots: {
                pending: {
                    prompt: "Tu computadora se muere en este instante. Haz clic en lo que te dice exactamente cuánto trabajo perderías.",
                    label: "Todavía no está en el historial — esto es lo que se pierde",
                    miss: "Eso ya está guardado. Busca lo que todavía NO lo está.",
                },
                commit: {
                    prompt: "El botón de borrar por fin funciona. Haz clic en lo que presionas para poder volver a este momento exacto siempre que quieras.",
                    label: "Guarda una versión a la que puedes volver",
                },
                message: {
                    prompt: "Es dentro de seis meses y estás buscando una versión. Haz clic en lo único de esa fila que te dice qué cambió.",
                    label: "La nota para tu yo del futuro",
                    miss: "Eso identifica la versión, pero no te dice qué tiene dentro.",
                },
                restore: {
                    prompt: "Pasaste la mañana empeorando la página. Haz clic en lo que te devuelve la versión de ayer.",
                    label: "Tus archivos vuelven a esa versión",
                },
                branch: {
                    prompt: "Quieres probar un diseño completamente distinto sin arriesgar la versión que ya funciona. Haz clic en lo que te deja hacerlo aparte.",
                    label: "Una línea de trabajo aparte",
                },
            },
            exercise: `Esta es una pantalla de historial de versiones. Responde las cinco preguntas de arriba haciendo clic en la parte de la pantalla que cada una describe.`,
            hint: "Aquí no se escribe nada. En cada pregunta piensa qué necesitas: guardar algo, encontrar algo, volver atrás, o trabajar aparte sin arriesgar lo que ya funciona.",
        },
        en: {
            title: "Version history",
            pendingHead: "right now",
            pending: "3 files with unsaved changes",
            saveHead: "save a version",
            msgPlaceholder: "Added the delete button to the list",
            commit: "Commit",
            branch: "main",
            historyHead: "saved versions",
            h3: "a1b2c3",
            d3: "Added the delete button to the list",
            t3: "2 minutes ago",
            h2: "9f4e21",
            d2: "Prices now survive a reload",
            t2: "yesterday",
            h1: "7c0d55",
            d1: "First version of my app",
            t1: "10 days ago",
            restore: "Restore",
            stat: "+12 −1",
            spots: {
                pending: {
                    prompt: "Your computer dies this second. Click the thing that tells you exactly how much work you would lose.",
                    label: "Not in the history yet — this is what goes",
                    miss: "That one is already saved. Look for what still isn't.",
                },
                commit: {
                    prompt: "The delete button finally works. Click what you press to make this exact moment something you can always come back to.",
                    label: "Saves a version you can return to",
                },
                message: {
                    prompt: "It is six months from now and you are hunting for a version. Click the only thing on that row that tells you what changed.",
                    label: "The note to your future self",
                    miss: "That identifies the version, but it doesn't tell you what is in it.",
                },
                restore: {
                    prompt: "You spent all morning making the page worse. Click what gives you yesterday's version back.",
                    label: "Your files go back to that version",
                },
                branch: {
                    prompt: "You want to try a completely different design without risking the version that already works. Click what lets you do it separately.",
                    label: "A separate line of work",
                },
            },
            exercise: `This is a version history screen. Answer the five questions above by clicking the part of the screen each one describes.`,
            hint: "Nothing gets typed here. For each question, think about what you need: to save something, to find something, to go back, or to work on something risky off to one side.",
        },
    },

    // V2512 — a repository page on github.com, the second Module 5 replica and the
    // only screen in the course built on `browserScreen` rather than the dark app
    // window. It is a web page, and the address bar is half the lesson: where the
    // CODE lives and where the APP runs become two different addresses in V2513, and
    // students conflate them.
    //
    // Three of the nine marked regions are never asked. The account avatar is the
    // important one — "Settings is this repository's, not yours" is a heading in the
    // prose, and it only lands if clicking the avatar answers back instead of doing
    // nothing. `issues` and `appjs` are there so a plausible wrong click is a wrong
    // answer rather than dead space.
    V2512: {
        es: {
            url: "github.com/maria-tacos/mi-app",
            owner: "maria-tacos /",
            repo: "mi-app",
            vis: "Public",
            tabCode: "Code",
            tabIssues: "Issues",
            tabSettings: "Settings",
            branch: "main",
            commits: "12 commits",
            who: "maria-tacos",
            msg: "Agregué el botón de borrar a la lista",
            when: "hace 2 minutos",
            files: [
                { name: "index.html", msg: "Agregué el botón de borrar a la lista", when: "hace 2 minutos" },
                { name: "app.html", msg: "Primera versión de mi app", when: "hace 10 días" },
                { name: "styles.css", msg: "Cambié el color a naranja", when: "hace 3 días" },
                { name: "config.js", msg: "Primera versión de mi app", when: "hace 10 días" },
                { name: "app.js", msg: "Los precios ahora se guardan al recargar", when: "ayer" },
                { name: "README.md", msg: "Primera versión de mi app", when: "hace 10 días" },
            ],
            readmeH: "mi-app",
            readmeP: "Mi app para tomar pedidos. Ábrela con index.html.",
            spots: {
                url: {
                    // Never phrase this one as "copy and send". It made students
                    // drag across the text to select it, and a drag is not a click —
                    // see the note on user-select in hotspotHarness.js.
                    prompt: "Una amiga desarrolladora quiere leer tu código y te pregunta dónde está. Haz clic en lo único de esta pantalla que responde esa pregunta.",
                    label: "Dónde vive tu código — no dónde corre tu app",
                    miss: "Eso está en la página. Lo que buscas es la dirección de la página.",
                },
                indexfile: {
                    prompt: "Cuando publiques, un visitante que llegue a tu dirección va a ver un archivo antes que cualquier otro. Haz clic en ese archivo.",
                    label: "El archivo que se sirve primero",
                    miss: "Ese archivo también está en tu app, pero no es el que abre el navegador al llegar.",
                },
                lastcommit: {
                    prompt: "Vuelves después de dos semanas y no te acuerdas en qué te quedaste. Haz clic en lo único de ese renglón que te lo dice.",
                    label: "Tu mensaje de commit, trabajando en público",
                    miss: "Eso dice quién o cuándo. Ninguno de los dos te dice qué cambió.",
                },
                commits: {
                    prompt: "No quieres la última versión: las quieres todas, desde la primera. Haz clic en lo que abre la lista completa.",
                    label: "Todas tus versiones guardadas, de la más nueva a la más vieja",
                    miss: "Eso te muestra la más reciente y nada más.",
                },
                branch: {
                    prompt: "En la siguiente lección, al publicar, te van a preguntar qué línea de trabajo poner en línea. Haz clic en la respuesta que vas a dar.",
                    label: "La rama que vas a publicar",
                },
                settings: {
                    prompt: "Tu código está aquí pero tu app sigue sin estar en internet. Haz clic en la pestaña donde vive el interruptor que la publica.",
                    label: "La configuración de ESTE repositorio — adentro está Pages",
                    miss: "Eso es tu cuenta, o es el código. Lo que buscas es la configuración del repositorio.",
                },
            },
            exercise: `Esta es la página de tu repositorio en GitHub. Responde las seis preguntas de arriba haciendo clic en la parte de la página que cada una describe.`,
            hint: "Aquí no se escribe nada. Si dudas, lee el renglón gris que está arriba de la lista de archivos: casi todas las respuestas están ahí o justo a su alrededor.",
        },
        en: {
            url: "github.com/maria-tacos/my-app",
            owner: "maria-tacos /",
            repo: "my-app",
            vis: "Public",
            tabCode: "Code",
            tabIssues: "Issues",
            tabSettings: "Settings",
            branch: "main",
            commits: "12 commits",
            who: "maria-tacos",
            msg: "Added the delete button to the list",
            when: "2 minutes ago",
            files: [
                { name: "index.html", msg: "Added the delete button to the list", when: "2 minutes ago" },
                { name: "app.html", msg: "First version of my app", when: "10 days ago" },
                { name: "styles.css", msg: "Changed the colour to orange", when: "3 days ago" },
                { name: "config.js", msg: "First version of my app", when: "10 days ago" },
                { name: "app.js", msg: "Prices now survive a reload", when: "yesterday" },
                { name: "README.md", msg: "First version of my app", when: "10 days ago" },
            ],
            readmeH: "my-app",
            readmeP: "My app for taking orders. Open it with index.html.",
            spots: {
                url: {
                    // Never phrase this one as "copy and send". It made students
                    // drag across the text to select it, and a drag is not a click —
                    // see the note on user-select in hotspotHarness.js.
                    prompt: "A developer friend wants to read your code and asks where it is. Click the one thing on this screen that answers that.",
                    label: "Where your code lives — not where your app runs",
                    miss: "That is on the page. You want the address of the page.",
                },
                indexfile: {
                    prompt: "Once you publish, a visitor arriving at your address sees one file before any other. Click that file.",
                    label: "The file that gets served first",
                    miss: "That file is part of your app too, but it is not the one a browser opens on arrival.",
                },
                lastcommit: {
                    prompt: "You come back after two weeks and cannot remember where you left off. Click the only thing on that row that tells you.",
                    label: "Your commit message, doing its job in public",
                    miss: "That says who, or when. Neither one tells you what changed.",
                },
                commits: {
                    prompt: "You do not want the newest version — you want all of them, back to the first. Click what opens the full list.",
                    label: "Every version you saved, newest first",
                    miss: "That shows you the most recent one and nothing else.",
                },
                branch: {
                    prompt: "In the next lesson, publishing asks which line of work to put online. Click the answer you are going to give it.",
                    label: "The branch you will publish",
                },
                settings: {
                    prompt: "Your code is here but your app is still not on the internet. Click the tab that holds the switch that publishes it.",
                    label: "THIS repository's settings — Pages is inside",
                    miss: "That is your account, or it is the code. You want the repository's own settings.",
                },
            },
            exercise: `This is your repository page on GitHub. Answer the six questions above by clicking the part of the page each one describes.`,
            hint: "Nothing gets typed here. When you are unsure, read the grey line sitting above the file list — most of the answers are in it or right around it.",
        },
    },

    // ── CD02 Module 6 ────────────────────────────────────────────────────────
    // The replica screens. Every visible word on them comes from here, so the checks
    // and the labels can never drift apart, and neither language is a translation
    // of a page that only exists in the other.
    //
    // Product names are NOT translated in either locale — Chat, Cowork, Code, Local,
    // Select folder, Manual, Accept edits, Plan, Auto, Haiku, Sonnet, Opus, Fable,
    // low, high, ultracode. They are things you look for on screen, so `textContains`
    // can safely assert on them: unlike ordinary words, they read identically in
    // Spanish and English, which is exactly why the checks are allowed to use them.

    // V2603 — the three tabs. `#tab-code` ships with no label, so the third check
    // (click it, expect #panel to fill) can only pass once the student names it.
    V2603: {
        es: {
            title: "La aplicación de Claude",
            tabChat: "Chat",
            tabCowork: "Cowork",
            tabCode: "Code",
            listHead: "qué hace cada una",
            dChat: "Conversación normal. NO toca tus archivos.",
            dCowork: "Trabaja solo, en segundo plano, en una máquina virtual aislada.",
            dCode: "Acceso directo a tus archivos locales. Tú apruebas cada cambio.",
            spots: {
                code: {
                    prompt: "Tienes un index.html en tu computadora y quieres que Claude lo edite ahí mismo. Haz clic en la pestaña que abres.",
                    label: "La única que llega a tus archivos",
                    miss: "Esa no toca tus archivos. Busca la que entra directo a la carpeta de tu proyecto.",
                },
                chat: {
                    prompt: "Todavía no quieres cambiar nada: solo platicar una idea y ver qué opina. Haz clic en la pestaña.",
                    label: "Conversación, sin archivos",
                },
                cowork: {
                    prompt: "Quieres encargarle un trabajo largo y cerrar la laptop mientras se hace. Haz clic en la pestaña.",
                    label: "Trabaja sola, en segundo plano",
                },
            },
            exercise: `Arriba de la ventana hay tres preguntas, una a la vez. Responde cada una haciendo clic en la pestaña que corresponde. La lista de abajo dice qué hace cada pestaña, por si necesitas repasarla.`,
            hint: "Aquí no se escribe nada: solo hacer clic. Piensa en qué necesita cada situación —¿toca archivos? ¿tiene que seguir sin ti?— y no en el nombre de la pestaña.",
        },
        en: {
            title: "The Claude app",
            tabChat: "Chat",
            tabCowork: "Cowork",
            tabCode: "Code",
            listHead: "what each one does",
            dChat: "Ordinary conversation. Does NOT touch your files.",
            dCowork: "Works on its own, in the background, in a sandboxed virtual machine.",
            dCode: "Direct access to your local files. You approve every change.",
            spots: {
                code: {
                    prompt: "You have an index.html sitting on your computer and you want Claude to edit it there. Click the tab you open.",
                    label: "The only one that reaches your files",
                    miss: "That one doesn't touch your files. Look for the one that goes straight into your project folder.",
                },
                chat: {
                    prompt: "You don't want to change anything yet — just talk an idea through and see what it thinks. Click the tab.",
                    label: "Conversation, no files",
                },
                cowork: {
                    prompt: "You want to hand off a long job and close the laptop while it happens. Click the tab.",
                    label: "Works on its own, in the background",
                },
            },
            exercise: `There are three questions above the window, one at a time. Answer each by clicking the tab it describes. The list below says what each tab does, if you need to check.`,
            hint: "Nothing to type here — just click. Think about what each situation needs — does it touch files? does it have to carry on without you? — rather than about the tab's name.",
        },
    },

    // V2604 — adding a local project. The step every tutorial skips.
    V2604: {
        es: {
            title: "Nueva sesión",
            envHead: "¿dónde corre esta sesión?",
            envLocal: "Local",
            envCloud: "Cloud",
            envSsh: "SSH",
            envWsl: "WSL",
            dLocal: "Tu máquina, usando tus archivos directamente.",
            dCloud: "Los servidores de Anthropic. Sigue aunque cierres la app.",
            dSsh: "Otra computadora, conectada por red.",
            dWsl: "En Windows: un entorno Linux dentro de tu PC.",
            folderHead: "carpeta del proyecto",
            folderShown: "mi-app",
            select: "Select folder",
            folderNote: "Claude solo ve lo que hay dentro de esta carpeta. Nada de afuera.",
            spots: {
                local: {
                    prompt: "Tu index.html está en la laptop que tienes enfrente. Haz clic en el entorno que necesitas.",
                    label: "Tu máquina, tus archivos",
                    miss: "Ese no usa los archivos de la computadora que tienes enfrente.",
                },
                select: {
                    prompt: "Ya elegiste el entorno. Haz clic en lo que presionas después para decirle a Claude en qué proyecto vas a trabajar.",
                    label: "Convierte una carpeta en tu proyecto",
                },
                cloud: {
                    prompt: "Quieres encargarle algo largo y que siga avanzando aunque cierres la aplicación. Haz clic en el entorno.",
                    label: "Sigue sin ti, en los servidores de Anthropic",
                },
                ssh: {
                    prompt: "Tu proyecto no está aquí: vive en otra computadora a la que te conectas por red. Haz clic en el entorno.",
                    label: "Otra computadora, por red",
                },
            },
            exercise: `Esta es la pantalla de una sesión nueva, el paso que casi todos los tutoriales se saltan. Responde las cuatro preguntas de arriba haciendo clic en la parte que cada una describe.`,
            hint: "Aquí no se escribe nada. Fíjate en DÓNDE están los archivos en cada situación: en tu computadora, en otra máquina, o en ninguna de las dos.",
        },
        en: {
            title: "New session",
            envHead: "where does this session run?",
            envLocal: "Local",
            envCloud: "Cloud",
            envSsh: "SSH",
            envWsl: "WSL",
            dLocal: "Your machine, using your files directly.",
            dCloud: "Anthropic's servers. Keeps going even if you close the app.",
            dSsh: "Another computer, connected over the network.",
            dWsl: "On Windows: a Linux environment inside your PC.",
            folderHead: "project folder",
            folderShown: "my-app",
            select: "Select folder",
            folderNote: "Claude only sees what is inside this folder. Nothing outside it.",
            spots: {
                local: {
                    prompt: "Your index.html is on the laptop in front of you. Click the environment you need.",
                    label: "Your machine, your files",
                    miss: "That one doesn't use the files on the computer in front of you.",
                },
                select: {
                    prompt: "You've picked the environment. Click what you press next to tell Claude which project you'll be working on.",
                    label: "Turns a folder into your project",
                },
                cloud: {
                    prompt: "You want to hand off something long and have it keep going even if you close the app. Click the environment.",
                    label: "Carries on without you, on Anthropic's servers",
                },
                ssh: {
                    prompt: "Your project isn't here — it lives on another computer you connect to over the network. Click the environment.",
                    label: "Another computer, over the network",
                },
            },
            exercise: `This is the new-session screen, the step almost every tutorial skips. Answer the four questions above by clicking the part each one describes.`,
            hint: "Nothing to type here. Pay attention to WHERE the files are in each situation: on your computer, on another machine, or neither.",
        },
    },

    // V2605 — the whole window. This is the anatomy lesson, so the region comments
    // carry more of the teaching than anywhere else in the module.
    V2605: {
        es: {
            navBack: "‹",
            navFwd: "›",
            tabChat: "Chat",
            tabCowork: "Cowork",
            tabCode: "Code",
            newSession: "+ New session",
            navArtifacts: "Artefactos",
            navCustomize: "Personalizar",
            navMore: "Más",
            sessionsHead: "Sesiones",
            sessionOn: "Mi app: primeros cambios",
            session2: "Arreglar el botón de borrar",
            session3: "Poner mi app en línea",
            account: "tu cuenta · plan Max",
            title: "Mi app: primeros cambios",
            actLabel: "lo que Claude está haciendo:",
            actRead: "Leyendo index.html",
            actSearch: "Buscando la lista en la página",
            actEdit: "Preparando un cambio",
            branch: "DEV",
            statPlus: "+128",
            statMinus: "−4",
            pr: "Create PR",
            promptExample: "agrega un botón de borrar a cada elemento de la lista",
            send: "Enviar",
            stop: "Detener",
            mode: "Manual",
            modelName: "Opus 5",
            effortName: "high",
            addLabel: "+ adjuntar",
            micLabel: "🎙",
            spots: {
                thread: {
                    prompt: "Claude lleva un minuto trabajando y quieres ver qué ha hecho hasta ahora. Haz clic en la zona donde te lo va contando, paso a paso.",
                    label: "La conversación: cada paso, mientras trabaja",
                },
                box: {
                    prompt: "Quieres pedirle un cambio con tus palabras, sin comandos. Haz clic en donde se escribe.",
                    label: "El cuadro de texto: tu petición, en frases normales",
                },
                sessions: {
                    prompt: "Ayer dejaste una conversación a medias y quieres volver a ella. Haz clic en donde la encuentras.",
                    label: "Tus sesiones: cada una con su propio contexto",
                },
                branch: {
                    prompt: "Quieres confirmar que no estás tocando la versión buena de tu proyecto. Haz clic en lo que te dice en qué versión estás trabajando.",
                    label: "La rama: la versión en la que trabajas",
                },
                stat: {
                    prompt: "Antes de aceptar nada quieres saber cuánto de tu proyecto ha cambiado ya. Haz clic en el dato que te lo dice.",
                    label: "Cuánto ha cambiado: líneas agregadas y quitadas",
                    miss: "Casi: eso dice DÓNDE estás trabajando, no cuánto ha cambiado.",
                },
                mode: {
                    prompt: "Quieres decidir si Claude te pregunta antes de cambiar un archivo. Haz clic en el control que manda eso.",
                    label: "El modo de permisos: si pregunta o no",
                },
            },
            exercise: `Esta es una réplica de la ventana de una sesión de Claude Code. Responde las preguntas de arriba haciendo clic en la parte de la ventana que cada una describe. Son seis: cuando encuentres las seis, la lección queda completa.`,
            hint: "Aquí no se escribe nada: solo mirar y hacer clic. Equivocarte no cuesta nada, puedes volver a intentarlo. Lee cada pregunta pensando para qué SIRVE cada zona, no cómo se llama.",
        },
        en: {
            navBack: "‹",
            navFwd: "›",
            tabChat: "Chat",
            tabCowork: "Cowork",
            tabCode: "Code",
            newSession: "+ New session",
            navArtifacts: "Artifacts",
            navCustomize: "Customize",
            navMore: "More",
            sessionsHead: "Sessions",
            sessionOn: "My app: first changes",
            session2: "Fix the delete button",
            session3: "Put my app online",
            account: "your account · Max plan",
            title: "My app: first changes",
            actLabel: "what Claude is doing:",
            actRead: "Reading index.html",
            actSearch: "Looking for the list on the page",
            actEdit: "Preparing a change",
            branch: "DEV",
            statPlus: "+128",
            statMinus: "−4",
            pr: "Create PR",
            promptExample: "add a delete button to each item on the list",
            send: "Send",
            stop: "Stop",
            mode: "Manual",
            modelName: "Opus 5",
            effortName: "high",
            addLabel: "+ attach",
            micLabel: "🎙",
            spots: {
                thread: {
                    prompt: "Claude has been working for a minute and you want to see what it has done so far. Click the area where it tells you, step by step.",
                    label: "The conversation: every step, as it works",
                },
                box: {
                    prompt: "You want to ask for a change in your own words, no commands. Click where you type it.",
                    label: "The prompt box: your request, in plain sentences",
                },
                sessions: {
                    prompt: "You left a conversation half-finished yesterday and want to go back to it. Click where you find it.",
                    label: "Your sessions: each with its own context",
                },
                branch: {
                    prompt: "You want to be sure you are not touching the good version of your project. Click what tells you which version you are working on.",
                    label: "The branch: the version you are working on",
                },
                stat: {
                    prompt: "Before you accept anything you want to know how much of your project has already changed. Click the figure that tells you.",
                    label: "How much changed: lines added and removed",
                    miss: "Close — that says WHERE you are working, not how much has changed.",
                },
                mode: {
                    prompt: "You want to decide whether Claude asks you before it changes a file. Click the control that governs that.",
                    label: "The permission mode: whether it asks first",
                },
            },
            exercise: `This is a replica of a Claude Code session window. Answer the questions above it by clicking the part of the window each one describes. There are six — find all six and the lesson is complete.`,
            hint: "Nothing to type here — just look and click. A wrong click costs you nothing, so try again. Read each question thinking about what a region is FOR, not what it is called.",
        },
    },

    // V2606 — the model menu, opened from next to the send button.
    V2606: {
        es: {
            title: "Elegir modelo",
            shortcut: "Ctrl+Shift+I",
            head: "la familia, de menor a mayor",
            kHaiku: "Haiku",
            kSonnet: "Sonnet",
            kOpus: "Opus",
            kFable: "Fable",
            dHaiku: "Rápido y económico. Tareas simples y mecánicas.",
            dSonnet: "Muy buen generalista. La opción diaria.",
            dOpus: "Razonamiento complejo, cuando hay que pensar de verdad.",
            dFable: "El más capaz. Tus tareas más difíciles y largas.",
            ringNote: "El anillo junto al selector muestra cuánta memoria lleva la sesión.",
            spots: {
                fable: {
                    prompt: "Vas a pedirle un cambio grande, que toca varios archivos a la vez, y quieres el mejor razonamiento que haya. Haz clic en la familia.",
                    label: "Lo más capaz, para lo más difícil",
                    miss: "Esa es buenísima para el día a día, pero no es la más capaz de la lista.",
                },
                haiku: {
                    prompt: "Solo quieres cambiarle el nombre a un botón, y lo quieres barato y al instante. Haz clic en la familia.",
                    label: "Rápida y económica",
                },
                shortcut: {
                    prompt: "A media sesión te das cuenta de que la tarea es más difícil de lo que creías. Haz clic en lo que te dice cómo cambiar de modelo sin empezar la conversación de nuevo.",
                    label: "El atajo: cambia de modelo a media sesión",
                },
            },
            exercise: `Este es el menú de modelos, el que se abre junto al botón de enviar. Responde las tres preguntas de arriba haciendo clic en la fila que corresponde. Las descripciones empiezan borrosas y se van destapando cuando aciertas.`,
            hint: "Las familias están ordenadas de menor a mayor capacidad: la primera es la más rápida y barata, la última la más capaz. El número de versión solo dice de qué generación es.",
        },
        en: {
            title: "Choose model",
            shortcut: "Ctrl+Shift+I",
            head: "the family, smallest to largest",
            kHaiku: "Haiku",
            kSonnet: "Sonnet",
            kOpus: "Opus",
            kFable: "Fable",
            dHaiku: "Fast and cheap. Simple, mechanical tasks.",
            dSonnet: "A really good generalist. The everyday choice.",
            dOpus: "Complex reasoning, when it needs actual thought.",
            dFable: "The most capable. Your hardest and longest tasks.",
            ringNote: "The ring next to the picker shows how much memory the session has used.",
            spots: {
                fable: {
                    prompt: "You're about to ask for a big change that touches several files at once, and you want the strongest reasoning there is. Click the family.",
                    label: "The most capable, for the hardest work",
                    miss: "That one is excellent day to day, but it isn't the most capable in the list.",
                },
                haiku: {
                    prompt: "You only want to rename a button, and you want it cheap and instant. Click the family.",
                    label: "Fast and cheap",
                },
                shortcut: {
                    prompt: "Halfway through a session you realize the task is harder than you thought. Click what tells you how to switch models without starting the conversation over.",
                    label: "The shortcut: change model mid-session",
                },
            },
            exercise: `This is the model menu, the one that opens next to the send button. Answer the three questions above by clicking the matching row. The descriptions start blurred and uncover as you get each one right.`,
            hint: "The families are ordered from least to most capable: the first is the fastest and cheapest, the last the most capable. The version number only tells you which generation it is.",
        },
    },

    // V2607 — the effort menu. `ultracode` is deliberately the answer to TODO 1:
    // it is the one thing in this menu that is NOT a model effort level.
    V2607: {
        es: {
            title: "Nivel de esfuerzo",
            shortcut: "Ctrl+Shift+E",
            head: "cuánto trabajo hace antes de volver a consultarte",
            kLow: "low",
            kMedium: "medium",
            kHigh: "high",
            kXhigh: "xhigh",
            kMax: "max",
            kUltra: "ultracode",
            dLow: "Tareas cortas y simples, donde importa la velocidad.",
            dMedium: "Más económico, cediendo algo de capacidad.",
            dHigh: "El equilibrio. Es el valor por defecto.",
            dXhigh: "Razonamiento más profundo, con más gasto.",
            dMax: "El más profundo. Tiende a pensar de más. Solo esta sesión.",
            dUltra: "No es un nivel del modelo: es una opción de Claude Code. Planifica un flujo de trabajo. Solo esta sesión.",
            defaultNote: "Si no tocas nada, la sesión corre en high.",
            spots: {
                ultra: {
                    prompt: "Cinco de estos seis son niveles de esfuerzo del modelo. Uno no lo es: es una opción de Claude Code. Haz clic en ese.",
                    label: "Opción de Claude Code, no nivel del modelo",
                    miss: "Ese sí es un nivel del modelo. Busca el que no encaja en la lista.",
                },
                low: {
                    prompt: "Una pregunta corta y simple, y quieres la respuesta rápida y barata. Haz clic en el nivel.",
                    label: "Lo más rápido y económico",
                },
                max: {
                    prompt: "Un problema espinoso donde prefieres que piense todo lo que pueda antes de contestarte. Haz clic en el nivel.",
                    label: "El más profundo; solo esta sesión",
                },
            },
            exercise: `Este es el menú de esfuerzo: cuánto trabaja Claude antes de volver a consultarte. Responde las tres preguntas de arriba haciendo clic en la fila que corresponde.`,
            hint: "La lista va de menos a más esfuerzo. Uno de los seis se cuela: no describe cuánto piensa el modelo, sino algo que hace Claude Code. Ese es el intruso.",
        },
        en: {
            title: "Effort level",
            shortcut: "Ctrl+Shift+E",
            head: "how much work it does before checking back with you",
            kLow: "low",
            kMedium: "medium",
            kHigh: "high",
            kXhigh: "xhigh",
            kMax: "max",
            kUltra: "ultracode",
            dLow: "Short, simple tasks where speed matters.",
            dMedium: "Cheaper, trading off some capability.",
            dHigh: "The balance. This is the default.",
            dXhigh: "Deeper reasoning, at a higher spend.",
            dMax: "The deepest. Prone to overthinking. This session only.",
            dUltra: "Not a model level: a Claude Code setting. Plans a workflow. This session only.",
            defaultNote: "If you touch nothing, the session runs at high.",
            spots: {
                ultra: {
                    prompt: "Five of these six are model effort levels. One is not — it is a Claude Code setting. Click that one.",
                    label: "A Claude Code setting, not a model level",
                    miss: "That one really is a model level. Look for the one that doesn't belong in the list.",
                },
                low: {
                    prompt: "A short, simple question, and you want the answer fast and cheap. Click the level.",
                    label: "The fastest and cheapest",
                },
                max: {
                    prompt: "A thorny problem where you'd rather it thought as hard as it can before answering. Click the level.",
                    label: "The deepest; this session only",
                },
            },
            exercise: `This is the effort menu: how much work Claude does before checking back with you. Answer the three questions above by clicking the matching row.`,
            hint: "The list runs from least to most effort. One of the six is a stowaway: it doesn't describe how hard the model thinks, but something Claude Code does. That's the odd one out.",
        },
    },

    // V2608 — the permission mode selector. The Shift+Tab warning is on the page on
    // purpose: it is the single most common thing a tutorial will get wrong.
    V2608: {
        es: {
            title: "Modo de permisos",
            shortcut: "Ctrl+Shift+M",
            head: "cuánto hace sin preguntarte",
            kManual: "Manual",
            kAccept: "Accept edits",
            kPlan: "Plan",
            kAuto: "Auto",
            kBypass: "Bypass permissions",
            dManual: "Pregunta antes de editar un archivo o ejecutar un comando. Es el valor por defecto.",
            dAccept: "Aplica ediciones sin preguntar; sigue preguntando por lo demás.",
            dPlan: "Te muestra lo que haría, sin cambiar absolutamente nada.",
            dAuto: "Actúa con verificaciones de seguridad en segundo plano.",
            dBypass: "Sin confirmaciones. Solo para máquinas virtuales desechables.",
            warn: "Shift+Tab NO cambia el modo en la aplicación de escritorio: eso es la versión de terminal.",
            memoryNote: "El modo se recuerda por carpeta. Plan es la excepción: dura solo esta sesión.",
            spots: {
                manual: {
                    prompt: "Acabas de abrir un proyecto que te importa y todavía no te fías de ti mismo con esta herramienta. Haz clic en el modo en el que empiezas.",
                    label: "Pregunta antes de cada cambio; es el de fábrica",
                    miss: "Ese ya aplica ediciones sin preguntarte. Busca el que pregunta ANTES de cada cambio.",
                },
                plan: {
                    prompt: "Antes de un cambio grande quieres ver el plan completo, sin que se toque un solo archivo. Haz clic en el modo.",
                    label: "Te muestra lo que haría; no cambia nada",
                },
                bypass: {
                    prompt: "Haz clic en el modo que NO deberías usar en un proyecto que te importa.",
                    label: "Sin confirmaciones; solo máquinas desechables",
                },
                toggle: {
                    prompt: "Un tutorial te dijo que Shift+Tab cambia el modo, y en la aplicación no te funciona. Haz clic en lo que te explica por qué.",
                    label: "Shift+Tab es de la terminal, no de esta ventana",
                },
            },
            exercise: `Este es el selector de modo de permisos: decide cuánto hace Claude sin preguntarte. Responde las cuatro preguntas de arriba haciendo clic en la parte que cada una describe.`,
            hint: "La lista va del que pregunta todo al que no pregunta nada. Mientras aprendes, el primero es tu lugar. Una de las cuatro respuestas no es un modo: es la advertencia en rojo.",
        },
        en: {
            title: "Permission mode",
            shortcut: "Ctrl+Shift+M",
            head: "how much it does without asking you",
            kManual: "Manual",
            kAccept: "Accept edits",
            kPlan: "Plan",
            kAuto: "Auto",
            kBypass: "Bypass permissions",
            dManual: "Asks before editing a file or running a command. This is the default.",
            dAccept: "Applies edits without asking; still asks about everything else.",
            dPlan: "Shows you what it would do, changing nothing at all.",
            dAuto: "Acts, with safety checks running in the background.",
            dBypass: "No prompts at all. Only for throwaway virtual machines.",
            warn: "Shift+Tab does NOT change the mode in the desktop app: that is the terminal version.",
            memoryNote: "The mode is remembered per folder. Plan is the exception: it lasts this session only.",
            spots: {
                manual: {
                    prompt: "You have just opened a project you care about, and you don't fully trust yourself with this tool yet. Click the mode you start in.",
                    label: "Asks before every change; the default",
                    miss: "That one already applies edits without asking you. Look for the one that asks BEFORE every change.",
                },
                plan: {
                    prompt: "Before a big change you want to see the whole plan, without a single file being touched. Click the mode.",
                    label: "Shows what it would do; changes nothing",
                },
                bypass: {
                    prompt: "Click the mode you should NOT use on a project you care about.",
                    label: "No prompts at all; throwaway machines only",
                },
                toggle: {
                    prompt: "A tutorial told you Shift+Tab changes the mode, and it isn't working in the app. Click what explains why.",
                    label: "Shift+Tab belongs to the terminal, not this window",
                },
            },
            exercise: `This is the permission mode selector: it decides how much Claude does without asking you. Answer the four questions above by clicking the part each one describes.`,
            hint: "The list runs from asking about everything to asking about nothing. While you're learning, the first one is where you belong. One of the four answers isn't a mode at all — it's the warning in red.",
        },
    },

    // V2609 — the approval moment. The diff lines are HTML-escaped on purpose; see
    // the note beside them.
    V2609: {
        es: {
            title: "Revisar el cambio",
            askTitle: "¿Aplico este cambio?",
            fileHead: "archivo",
            // Escapado a propósito: esto es un diff, así que las etiquetas tienen que
            // VERSE como código. Escritas como <li>/<button> de verdad, el navegador
            // las construiría en vez de imprimirlas, y el estudiante vería una lista
            // ya renderizada donde debería estar el cambio propuesto.
            diffPath: "index.html",
            diffKeep: "  &lt;li&gt;Tacos al pastor&lt;/li&gt;",
            diffAdd: "+ &lt;li&gt;Tacos al pastor &lt;button&gt;Borrar&lt;/button&gt;&lt;/li&gt;",
            asked: "le pedí: un botón de borrar en cada elemento de la lista",
            yes: "Aceptar",
            no: "Rechazar",
            stat: "+12 −1",
            statNote: "Presiona este indicador para abrir el diff completo, archivo por archivo.",
            twoQ: "Las dos preguntas: ¿es el archivo correcto? ¿es más o menos lo que pedí?",
            spots: {
                file: {
                    prompt: "Antes de aprobar nada quieres asegurarte de que va a tocar el archivo correcto. Haz clic en donde te lo dice.",
                    label: "El archivo que va a cambiar",
                },
                add: {
                    prompt: "Haz clic en la línea que Claude quiere agregar, la que todavía no existe en tu página.",
                    label: "La línea nueva, la que se agrega",
                    miss: "Esa línea ya está en tu página. Busca la que empieza con un +.",
                },
                reject: {
                    prompt: "Lees el cambio y no es lo que pediste: Claude entendió mal. Haz clic en lo que presionas.",
                    label: "Rechazar: es normal, es una conversación",
                },
                stat: {
                    prompt: "Quieres ver cuánto ha cambiado en total, archivo por archivo, no solo este pedazo. Haz clic en el indicador.",
                    label: "Cuánto cambió en total",
                },
            },
            exercise: `Este es el momento de aprobar: nada de tu computadora cambia hasta que respondas. Responde las cuatro preguntas de arriba haciendo clic en la parte que cada una describe.`,
            hint: "En un diff, la línea que empieza con + es la que se agrega; la de arriba es la que ya tienes. Compara siempre el cambio con lo que TÚ pediste, que está escrito ahí mismo.",
        },
        en: {
            title: "Review the change",
            askTitle: "Apply this change?",
            fileHead: "file",
            // See the note on the Spanish diff above — these must print as code.
            diffPath: "index.html",
            diffKeep: "  &lt;li&gt;Tacos al pastor&lt;/li&gt;",
            diffAdd: "+ &lt;li&gt;Tacos al pastor &lt;button&gt;Delete&lt;/button&gt;&lt;/li&gt;",
            asked: "I asked for: a delete button on each item in the list",
            yes: "Accept",
            no: "Reject",
            stat: "+12 −1",
            statNote: "Press this indicator to open the full diff, file by file.",
            twoQ: "The two questions: is it the right file? is this roughly what I asked for?",
            spots: {
                file: {
                    prompt: "Before you approve anything you want to be sure it is going to touch the right file. Click where it tells you.",
                    label: "The file that will change",
                },
                add: {
                    prompt: "Click the line Claude wants to add — the one that isn't on your page yet.",
                    label: "The new line, the one being added",
                    miss: "That line is already on your page. Look for the one starting with a +.",
                },
                reject: {
                    prompt: "You read the change and it isn't what you asked for — Claude misunderstood. Click what you press.",
                    label: "Reject: normal, it's a conversation",
                },
                stat: {
                    prompt: "You want to see how much has changed in total, file by file, not just this one piece. Click the indicator.",
                    label: "How much changed in total",
                },
            },
            exercise: `This is the moment of approval: nothing on your computer changes until you answer. Answer the four questions above by clicking the part each one describes.`,
            hint: "In a diff, the line starting with + is the one being added; the one above it is what you already have. Always compare the change against what YOU asked for, which is written right there.",
        },
    },

    // ── CD03 Module 3 ────────────────────────────────────────────────────────
    V3301: {
        es: {
            intro: "Esto ya es una página web de verdad. No hay nada más escondido.",
            todo: `TODO: escribe el nombre de tu app entre <h1> y </h1>,
       y una frase corta sobre ella entre <p id="frase"> y </p>.
       Después pulsa Ejecutar.`,
            scriptNote: "Todavía vacío. Esta parte llega en la lección 4.",
            checks: {
                title: "Escribiste el nombre de tu app en el título",
                phrase: "Escribiste una frase sobre tu app",
            },
            exercise: `Escribe el nombre de tu app entre <h1> y </h1>, y una frase corta sobre ella entre <p id="frase"> y </p>. Después pulsa Ejecutar.`,
            hint: "El texto va ENTRE la etiqueta de apertura y la de cierre, nunca dentro de los signos < >.",
        },
        en: {
            intro: "This is already a real web page. Nothing else is hidden.",
            todo: `TODO: write your app's name between <h1> and </h1>,
       and a short sentence about it between <p id="frase"> and </p>.
       Then press Run.`,
            scriptNote: "Still empty. That part arrives in lesson 4.",
            checks: {
                title: "You wrote your app's name in the title",
                phrase: "You wrote a sentence about your app",
            },
            exercise: `Write your app's name between <h1> and </h1>, and a short sentence about it between <p id="frase"> and </p>. Then press Run.`,
            hint: "The text goes BETWEEN the opening and closing tag, never inside the < > signs.",
        },
    },

    V3302: {
        es: {
            title: "Mi menú",
            intro: "Cada cosa que ves aquí está escrita abajo, envuelta en etiquetas.",
            todo: `TODO: pídele a Claude "un párrafo y un botón en HTML"
       y pega lo que te dé justo aquí, dentro de <body>.`,
            checks: {
                paragraph: "Ahora la página tiene un párrafo más",
                button: "Agregaste un botón a la página",
            },
            exercise: `Pídele a Claude "un párrafo y un botón en HTML" y pega lo que te dé dentro de <body>, donde está el comentario TODO. La página debe terminar con un párrafo más de los que tiene ahora, y con un botón.`,
            hint: "Pega el código dentro de <body>, no dentro de <style> ni de <script>.",
        },
        en: {
            title: "My menu",
            intro: "Everything you see here is written below, wrapped in tags.",
            todo: `TODO: ask Claude for "a paragraph and a button in HTML"
       and paste what it gives you right here, inside <body>.`,
            checks: {
                paragraph: "The page now has one more paragraph",
                button: "You added a button to the page",
            },
            exercise: `Ask Claude for "a paragraph and a button in HTML" and paste what it gives you inside <body>, where the TODO comment is. The page should end up with one more paragraph than it has now, plus a button.`,
            hint: "Paste the code inside <body>, not inside <style> or <script>.",
        },
    },

    V3303: {
        es: {
            title: "Mi menú",
            paragraph: 'Este párrafo tiene la clase "destacado".',
            todo: `TODO: dos cambios, los dos aquí arriba:
     1. cambia el background-color de .destacado, por ejemplo a #DBEAFE
     2. cambia el font-size de #titulo a otro tamaño.`,
            checks: {
                background: "Cambiaste el color de fondo de la caja destacada",
                fontSize: "Cambiaste el tamaño del texto del título",
            },
            exercise: `Haz dos cambios dentro de <style>: cambia el background-color de .destacado a cualquier otro color (por ejemplo #DBEAFE), y cambia el font-size de #titulo a un tamaño distinto de 20px.`,
            hint: "Las dos reglas ya existen. Solo tienes que cambiar el valor que va después de los dos puntos.",
        },
        en: {
            title: "My menu",
            paragraph: 'This paragraph has the class "destacado".',
            todo: `TODO: two changes, both right above:
     1. change the background-color of .destacado, for example to #DBEAFE
     2. change the font-size of #titulo to a different size.`,
            checks: {
                background: "You changed the highlighted box's background color",
                fontSize: "You changed the size of the title text",
            },
            exercise: `Make two changes inside <style>: change the background-color of .destacado to any other color (for example #DBEAFE), and change the font-size of #titulo to something other than 20px.`,
            hint: "Both rules already exist. All you have to change is the value after the colon.",
        },
    },

    V3304: {
        es: {
            title: "Mi menú",
            button: "Saludar",
            wiring: "Esto le dice al botón qué hacer cuando alguien lo toca.",
            todo: `TODO: escribe aquí UNA línea que ponga un mensaje en #mensaje:
    //   document.getElementById('mensaje').textContent = 'tu mensaje';`,
            checks: {
                shows: "Al pulsar el botón aparece tu mensaje",
            },
            exercise: `Dentro del addEventListener, escribe una línea que ponga un mensaje en #mensaje. Después pulsa Ejecutar y toca el botón "Saludar".`,
            hint: "Usa document.getElementById('mensaje').textContent = 'tu mensaje';",
        },
        en: {
            title: "My menu",
            button: "Say hello",
            wiring: "This tells the button what to do when someone taps it.",
            todo: `TODO: write ONE line here that puts a message into #mensaje:
    //   document.getElementById('mensaje').textContent = 'your message';`,
            checks: {
                shows: "Pressing the button makes your message appear",
            },
            exercise: `Inside the addEventListener, write one line that puts a message into #mensaje. Then press Run and tap the "Say hello" button.`,
            hint: "Use document.getElementById('mensaje').textContent = 'your message';",
        },
    },

    V3306: {
        es: {
            welcome: "Bienvenido a",
            dishesIntro: "Estos son los platillos de hoy:",
            todoList: "TODO (2 de 2): agrega un tercer platillo copiando una línea de arriba.",
            valueNote: "Un VALOR es algo que tu app se guarda para usarlo después.",
            todoValue: "TODO (1 de 2): escribe el nombre de tu app entre las comillas.",
            nameVar: "nombre",
            checks: {
                value: "Le diste un valor al nombre de tu app",
                list: "Tu lista ahora tiene tres elementos",
            },
            exercise: `Dos cambios: escribe el nombre de tu app entre las comillas de la variable nombre, y agrega un tercer platillo a la lista copiando una de las líneas <li> que ya están ahí.`,
            hint: "El tercer <li> va justo antes de </ul>, en su propia línea.",
        },
        en: {
            welcome: "Welcome to",
            dishesIntro: "These are today's dishes:",
            todoList: "TODO (2 of 2): add a third dish by copying one of the lines above.",
            valueNote: "A VALUE is something your app keeps so it can use it later.",
            todoValue: "TODO (1 of 2): write your app's name between the quotes.",
            nameVar: "appName",
            checks: {
                value: "You gave your app's name a value",
                list: "Your list now has three items",
            },
            exercise: `Two changes: write your app's name between the quotes of the appName variable, and add a third dish to the list by copying one of the <li> lines already there.`,
            hint: "The third <li> goes right before </ul>, on its own line.",
        },
    },

    V3307: {
        es: {
            title: "Tacos al pastor",
            todo: "TODO: cambia SOLO este número a 0 y vuelve a pulsar Ejecutar.",
            quantityVar: "cantidad",
            ifNote: `Un IF es una decisión: si pasa esto, haz esto; si no, haz lo otro.
  // Solo se ejecuta UNA de las dos mitades, nunca las dos.`,
            available: "Disponible",
            soldOut: "Agotado",
            checks: {
                soldOut: "Con cantidad en 0, el producto aparece como agotado",
                notAvailable: "Y ya no aparece como disponible",
            },
            exercise: `Cambia el número de la variable cantidad a 0 y vuelve a pulsar Ejecutar. Fíjate en cuál de las dos mitades del if se ejecuta ahora, y cuál dejó de ejecutarse.`,
            hint: "No cambies nada más: una sola cifra decide qué mitad del if corre.",
        },
        en: {
            title: "Tacos al pastor",
            todo: "TODO: change ONLY this number to 0 and press Run again.",
            quantityVar: "quantity",
            ifNote: `An IF is a decision: if this happens, do this; if not, do the other thing.
  // Only ONE of the two halves ever runs, never both.`,
            available: "Available",
            soldOut: "Sold out",
            checks: {
                soldOut: "With the quantity at 0, the dish shows as sold out",
                notAvailable: "And it no longer shows as available",
            },
            exercise: `Change the quantity variable to 0 and press Run again. Watch which half of the if runs now, and which one stopped running.`,
            hint: "Change nothing else — a single number decides which half of the if runs.",
        },
    },

    V3308: {
        es: {
            title: "Mi cuenta",
            priceLabel: "Precio:",
            currency: "pesos",
            button: "Calcular propina",
            priceVar: "precio",
            tipVar: "propina",
            brokenNote: "Pulsa el botón: no pasa nada. Algo de aquí abajo está mal escrito.",
            todo: `TODO: mira el error que aparece, cópialo tal cual y pídele a Claude
    // que te lo explique. Después arregla la línea.`,
            resultLabel: "Propina: ",
            checks: {
                works: "El botón ya calcula la propina y la muestra",
            },
            exercise: `Pulsa Ejecutar y luego el botón: no pasa nada. Lee el error que aparece, cópialo tal cual, pídele a Claude que te lo explique, y arregla la línea para que la propina se muestre.`,
            hint: "Compara con cuidado el id que usa el JavaScript con el id que de verdad existe en el HTML.",
        },
        en: {
            title: "My bill",
            priceLabel: "Price:",
            currency: "pesos",
            button: "Calculate tip",
            priceVar: "price",
            tipVar: "tip",
            brokenNote: "Press the button: nothing happens. Something down here is misspelled.",
            todo: `TODO: look at the error that appears, copy it exactly and ask Claude
    // to explain it. Then fix the line.`,
            resultLabel: "Tip: ",
            checks: {
                works: "The button now works out the tip and shows it",
            },
            exercise: `Press Run and then the button: nothing happens. Read the error that appears, copy it exactly, ask Claude to explain it, and fix the line so the tip shows up.`,
            hint: "Compare the id the JavaScript uses with the id that actually exists in the HTML.",
        },
    },

    // ── CD02 Module 3 ────────────────────────────────────────────────────────
    // Authored because the generator kept writing this lesson as a REFACTOR
    // ("replace the repeated onclick code with a function"), and a refactor is
    // behaviour-preserving by definition, so no DOM check can detect it. Reframed
    // as additive: one button already works, and the student writes the shared
    // function that makes the other two work.
    //
    // `hello` / `morning` / `welcome` are printed by the scaffold AND asserted by
    // the checks, which is exactly why they are single strings read by both.
    V2307: {
        es: {
            heading: "Saludos",
            intro: "Pulsa un botón para ver su saludo.",
            hello: "Hola",
            morning: "Buenos días",
            welcome: "Bienvenido",
            note: `El primer botón ya funciona, pero su código está escrito directamente
    // en el HTML. Si tuvieras 20 botones, repetirías eso 20 veces.`,
            todo: `TODO: escribe una función mostrarSaludo(mensaje) que ponga el mensaje
    // dentro de #salida, y úsala para que los otros dos botones funcionen.`,
            checks: {
                hello: 'El botón «Hola» muestra su saludo',
                morning: 'El botón «Buenos días» muestra su saludo',
                welcome: 'El botón «Bienvenido» muestra su saludo',
            },
            exercise: `El primer botón ya funciona, pero su código vive dentro del HTML. Escribe una función mostrarSaludo(mensaje) que ponga el mensaje dentro de #salida, y úsala para que los otros dos botones muestren también su saludo.`,
            hint: "Cada botón necesita su propio addEventListener, pero los tres pueden llamar a la misma función con un mensaje distinto.",
        },
        en: {
            heading: "Greetings",
            intro: "Press a button to see its greeting.",
            hello: "Hello",
            morning: "Good morning",
            welcome: "Welcome",
            note: `The first button already works, but its code is written straight
    // into the HTML. With 20 buttons you would repeat that 20 times.`,
            todo: `TODO: write a showGreeting(message) function that puts the message
    // inside #salida, and use it to make the other two buttons work.`,
            checks: {
                hello: 'The "Hello" button shows its greeting',
                morning: 'The "Good morning" button shows its greeting',
                welcome: 'The "Welcome" button shows its greeting',
            },
            exercise: `The first button already works, but its code lives inside the HTML. Write a showGreeting(message) function that puts the message inside #salida, and use it to make the other two buttons show their greeting too.`,
            hint: "Each button needs its own addEventListener, but all three can call the same function with a different message.",
        },
    },

    // ── CD02 Module 4 — the build chain ──────────────────────────────────────
    V2403: {
        es: {
            heading: "Bienvenido",
            intro: "Esta es la pantalla principal de tu app.",
            todo: `TODO: agrega aquí la pantalla principal de TU app.
         Dale un título con <h2> y explica en una frase qué hace.`,
            checks: {
                heading: "Tu pantalla principal tiene un título",
                second: "Agregaste una segunda sección a tu app",
            },
            exercise: `Agrega la pantalla principal de TU app donde está el comentario TODO: dale un título con <h2> y explica en una frase qué hace. Debe quedar como una segunda sección con class="card".`,
            hint: 'Copia la estructura del <div class="card"> que ya está arriba y cambia su contenido.',
        },
        en: {
            heading: "Welcome",
            intro: "This is your app's main screen.",
            todo: `TODO: add YOUR app's main screen here.
         Give it a title with <h2> and explain in one sentence what it does.`,
            checks: {
                heading: "Your main screen has a title",
                second: "You added a second section to your app",
            },
            exercise: `Add YOUR app's main screen where the TODO comment is: give it a title with <h2> and explain in one sentence what it does. It should end up as a second section with class="card".`,
            hint: 'Copy the <div class="card"> structure already above it and change what is inside.',
        },
    },

    V2404: {
        es: {
            heading: "Agregar algo nuevo",
            intro: "Escribe algo y presiona el botón.",
            placeholder: "Escribe aquí…",
            button: "Agregar",
            todo: `TODO: haz que el botón "Agregar" tome lo que hay en #entrada
    // y lo muestre como un <li class="item"> dentro de #lista.`,
            probe: "Prueba",
            checks: {
                appears: "Al escribir y pulsar Agregar, aparece un elemento en la lista",
                text: "El elemento muestra el texto que escribiste",
            },
            exercise: `Haz que el botón Agregar tome el texto de #entrada y lo muestre como un <li class="item"> dentro de #lista.`,
            hint: "Lee el texto con .value, crea el <li> con document.createElement y añádelo con appendChild.",
        },
        en: {
            heading: "Add something new",
            intro: "Type something and press the button.",
            placeholder: "Type here…",
            button: "Add",
            todo: `TODO: make the "Add" button take whatever is in #entrada
    // and show it as a <li class="item"> inside #lista.`,
            probe: "Test",
            checks: {
                appears: "Typing and pressing Add makes an item appear in the list",
                text: "The item shows the text you typed",
            },
            exercise: `Make the Add button take the text from #entrada and show it as a <li class="item"> inside #lista.`,
            hint: "Read the text with .value, create the <li> with document.createElement, and add it with appendChild.",
        },
    },

    V2405: {
        es: {
            heading: "Agregar algo nuevo",
            intro: "Lo que agregues debe seguir aquí mañana.",
            placeholder: "Escribe aquí…",
            button: "Agregar",
            addFn: "agregar",
            textVar: "texto",
            todo: `TODO: guarda la lista con localStorage.setItem('misDatos', ...)
      // para que siga aquí después de recargar.`,
            checks: {
                saved: "Lo que agregas queda guardado con localStorage",
                content: "Se guarda el texto que escribiste, no otra cosa",
            },
            exercise: `El botón ya agrega a la lista. Ahora guarda lo agregado con localStorage.setItem('misDatos', ...) para que siga ahí después de recargar la página.`,
            hint: "localStorage solo guarda texto: usa JSON.stringify para guardar y JSON.parse para volver a leer.",
        },
        en: {
            heading: "Add something new",
            intro: "Whatever you add should still be here tomorrow.",
            placeholder: "Type here…",
            button: "Add",
            addFn: "addItem",
            textVar: "text",
            todo: `TODO: save the list with localStorage.setItem('misDatos', ...)
      // so it is still here after a reload.`,
            checks: {
                saved: "What you add is saved with localStorage",
                content: "It saves the text you typed, not something else",
            },
            exercise: `The button already adds to the list. Now save what was added with localStorage.setItem('misDatos', ...) so it is still there after you reload the page.`,
            hint: "localStorage only stores text: use JSON.stringify to save and JSON.parse to read it back.",
        },
    },

    V2406: {
        es: {
            heading: "Mi lista",
            intro: "Todo lo que has guardado aparece aquí al abrir la app.",
            placeholder: "Escribe aquí…",
            button: "Agregar",
            paintFn: "pintar",
            savedFn: "guardados",
            textVar: "texto",
            dataVar: "datos",
            todo: `TODO: al abrir la app, recorre guardados() y pinta cada elemento,
    // para que la lista no aparezca vacía.`,
            checks: {
                onOpen: "Al abrir la app, tus datos guardados ya aparecen en la lista",
                real: "Se muestran los datos reales que estaban guardados",
            },
            exercise: `Al abrir la app, recorre lo que devuelve guardados() y pinta cada elemento dentro de #lista, para que la lista no aparezca vacía.`,
            hint: "forEach sobre guardados() y llama a pintar() con cada uno. No hace falta tocar el botón.",
        },
        en: {
            heading: "My list",
            intro: "Everything you have saved shows up here when the app opens.",
            placeholder: "Type here…",
            button: "Add",
            paintFn: "paint",
            savedFn: "saved",
            textVar: "text",
            dataVar: "data",
            todo: `TODO: when the app opens, loop over saved() and paint each item,
    // so the list does not show up empty.`,
            checks: {
                onOpen: "When the app opens, your saved data is already in the list",
                real: "It shows the real data that was saved",
            },
            exercise: `When the app opens, loop over what saved() returns and paint each item inside #lista, so the list does not show up empty.`,
            hint: "forEach over saved() and call paint() with each one. You do not need to touch the button.",
        },
    },

    // The echo phrase is printed by the stub AND asserted by the check, so both
    // read the same string.
    V2407: {
        es: {
            heading: "Pregúntale a la IA",
            intro: "Tu app también puede hablar con Claude.",
            placeholder: "Escribe tu pregunta…",
            button: "Preguntar",
            aiFn: "responderIA",
            aiArg: "pregunta",
            keyNote: `Esta función simula la respuesta de la IA. La llave de Claude NUNCA puede
    // vivir en el navegador — en tu app real, esta llamada la hace tu servidor.`,
            stubIntro: "Soy la función de IA de tu app.",
            echo: "Preguntaste",
            todo: `TODO: cuando presionen #preguntar, toma el texto de #pregunta,
    // pásalo por responderIA() y muestra el resultado dentro de #respuesta.`,
            checks: {
                answers: "Al pulsar Preguntar, aparece una respuesta",
                echoes: "La respuesta incluye lo que escribiste",
            },
            exercise: `Cuando pulsen #preguntar, toma el texto de #pregunta, pásalo por responderIA() y muestra lo que devuelve dentro de #respuesta.`,
            hint: "responderIA() ya está escrita: solo tienes que llamarla y poner su resultado en la página.",
        },
        en: {
            heading: "Ask the AI",
            intro: "Your app can talk to Claude too.",
            placeholder: "Type your question…",
            button: "Ask",
            aiFn: "aiReply",
            aiArg: "question",
            keyNote: `This function fakes the AI's answer. Claude's key can NEVER live in the
    // browser — in your real app, your server makes this call.`,
            // No apostrophes: this is interpolated into a single-quoted JS string.
            stubIntro: "I am the AI function inside your app.",
            echo: "You asked",
            todo: `TODO: when #preguntar is pressed, take the text from #pregunta,
    // pass it through aiReply() and show the result inside #respuesta.`,
            checks: {
                answers: "Pressing Ask makes an answer appear",
                echoes: "The answer includes what you typed",
            },
            exercise: `When #preguntar is pressed, take the text from #pregunta, pass it through aiReply() and show what it returns inside #respuesta.`,
            hint: "aiReply() is already written: you only have to call it and put its result on the page.",
        },
    },
};

/**
 * Checks for each authored lesson, written alongside its scaffold.
 *
 * These are deliberately NOT generated. The generator writes its own starterCode,
 * but the student actually receives the authored scaffold — so generated checks
 * end up pointing at elements that don't exist (a real bug caught by validating
 * against the scaffold: the generator invented `#jokeBtn`/`#joke` for V2407 while
 * the scaffold has `#preguntar`/`#respuesta`).
 *
 * Every check below must FAIL on the scaffold as written and pass only once the
 * student completes that lesson's TODO.
 *
 * Selectors are LOCALE-NEUTRAL — ids, classes, element counts and `:not(:empty)`,
 * never visible words — except where a check has to prove a specific string was
 * printed (V2307, V2407). Those read the string from the same COPY entry the
 * scaffold prints, so they hold in both languages.
 *
 * `:not(:empty)` is the workhorse: it means "the student put something here",
 * whatever they wrote and whatever language they wrote it in. It relies on the
 * scaffold's target elements being written as `<p id="x"></p>` with nothing at
 * all between the tags — even a newline would make them non-empty and pass
 * before the student touches anything.
 */
export const AUTHORED_TESTS = {
    // CD02 Module 6 has no checks at all any more. All seven replica lessons are
    // `hotspot` lessons: the student is given the FINISHED window and graded on
    // clicking the right part of it, not on filling blanks in its source. Their
    // screens and questions are in AUTHORED_SCREENS / AUTHORED_HOTSPOTS below, and
    // the reasoning for the switch is in the comment above them.

    // ── CD03 Module 3 ────────────────────────────────────────────────────────
    V3301: bilingual(COPY.V3301, (t) => [
        { description: t.checks.title, selector: "#titulo:not(:empty)", exists: true },
        { description: t.checks.phrase, selector: "#frase:not(:empty)", exists: true },
    ]),
    V3302: bilingual(COPY.V3302, (t) => [
        { description: t.checks.paragraph, selector: "body p", countAtLeast: 2 },
        { description: t.checks.button, selector: "body button", exists: true },
    ]),
    // Both checks compare against the value the scaffold ships with, rather than
    // against "transparent"/"unset". An element with no background computes
    // differently across engines, so `isNot` an explicit starting colour is the
    // version that means the same thing everywhere.
    V3303: bilingual(COPY.V3303, (t) => [
        { description: t.checks.background, selector: ".destacado", cssProperty: "background-color", isNot: "rgb(255, 255, 255)" },
        { description: t.checks.fontSize, selector: "#titulo", cssProperty: "font-size", isNot: "20px" },
    ]),
    V3304: bilingual(COPY.V3304, (t) => [
        { description: t.checks.shows, click: "#saludar", then: { selector: "#mensaje:not(:empty)", exists: true } },
    ]),
    V3306: bilingual(COPY.V3306, (t) => [
        { description: t.checks.value, selector: "#saludo:not(:empty)", exists: true },
        { description: t.checks.list, selector: "#lista li", countAtLeast: 3 },
    ]),
    // One edit — changing the quantity to 0 — flips both branches of the `if`, so
    // the second check is what teaches that the OTHER branch stopped running.
    V3307: bilingual(COPY.V3307, (t) => [
        { description: t.checks.soldOut, selector: "#estado.agotado", exists: true },
        { description: t.checks.notAvailable, selector: "#estado.disponible", exists: false },
    ]),
    V3308: bilingual(COPY.V3308, (t) => [
        { description: t.checks.works, click: "#calcular", then: { selector: "#resultado:not(:empty)", exists: true } },
    ]),

    // ── CD02 Module 3 ────────────────────────────────────────────────────────
    V2307: bilingual(COPY.V2307, (t) => [
        { description: t.checks.hello, click: "#btnHola", then: { selector: "#salida", textContains: t.hello } },
        { description: t.checks.morning, click: "#btnDias", then: { selector: "#salida", textContains: t.morning } },
        { description: t.checks.welcome, click: "#btnBienvenido", then: { selector: "#salida", textContains: t.welcome } },
    ]),

    // ── CD02 Module 4 ────────────────────────────────────────────────────────
    V2403: bilingual(COPY.V2403, (t) => [
        { description: t.checks.heading, selector: "main h2", exists: true },
        { description: t.checks.second, selector: "main .card", countAtLeast: 2 },
    ]),
    V2404: bilingual(COPY.V2404, (t) => [
        { description: t.checks.appears, type: { selector: "#entrada", value: t.probe }, click: "#agregar", then: { selector: "#lista .item", countAtLeast: 1 } },
        { description: t.checks.text, type: { selector: "#entrada", value: "Tacos" }, click: "#agregar", then: { selector: "#lista .item", textContains: "Tacos" } },
    ]),
    // The scaffold already adds to the list (that was V2404), so a DOM check would
    // pass before the student writes anything. Only a storage check can tell
    // whether the data was actually persisted.
    V2405: bilingual(COPY.V2405, (t) => [
        { description: t.checks.saved, type: { selector: "#entrada", value: "Rosa" }, click: "#agregar", then: { storageKey: "misDatos" } },
        { description: t.checks.content, type: { selector: "#entrada", value: "Tacos" }, click: "#agregar", then: { storageKey: "misDatos", contains: "Tacos" } },
    ]),
    // Seeded via AUTHORED_SEED_STORE below, so "show what's already saved" is
    // actually testable — with an empty store even a correct answer renders nothing.
    V2406: bilingual(COPY.V2406, (t) => [
        { description: t.checks.onOpen, selector: "#lista .item", countAtLeast: 2 },
        { description: t.checks.real, selector: "#lista .item", textContains: "Tacos" },
    ]),
    V2407: bilingual(COPY.V2407, (t) => [
        { description: t.checks.answers, type: { selector: "#pregunta", value: "Hola" }, click: "#preguntar", then: { selector: "#respuesta", textContains: t.echo } },
        { description: t.checks.echoes, type: { selector: "#pregunta", value: "Tacos" }, click: "#preguntar", then: { selector: "#respuesta", textContains: "Tacos" } },
    ]),
};

/**
 * Starting point for each authored lesson, keyed by lessonID.
 *
 * Module 4's are the previous lesson's finished app plus a clearly marked TODO —
 * the only part the student changes.
 */
export const AUTHORED_STARTERS = {
    // CD02 Module 6's replica screens are no longer starters. They are `hotspot`
    // lessons: the student receives the FINISHED window and is graded on clicking
    // the right part of it, so there is nothing here to edit. See AUTHORED_SCREENS.

    // ── CD03 Module 3 — reading pages ────────────────────────────────────────
    // Each one already works. The student changes ONE thing, presses Run, and sees
    // it. Anything longer than a line they ask Claude for and paste — that is the
    // skill the module is actually teaching.

    // V3301 — first look, first edit. Two empty elements to fill, nothing else,
    // so the very first contact with code ends in "I changed that, and it worked".
    V3301: bilingual(COPY.V3301, (t, lang) => miniPage({
        style: `  #titulo { color: #2563EB; font-size: 26px; margin-bottom: 4px; }
`,
        body: `  <h1 id="titulo"></h1>

  <p>${t.intro}</p>

  <!-- ${t.todo} -->
  <p id="frase"></p>

`,
        script: `  // ${t.scriptNote}
`,
    }, lang)),

    // V3302 — HTML holds what you see. They don't invent the markup: they ask
    // Claude for it and decide WHERE it goes, which is the actual lesson.
    V3302: bilingual(COPY.V3302, (t, lang) => miniPage({
        style: `  h1 { color: #2563EB; font-size: 24px; }
`,
        body: `  <h1>${t.title}</h1>

  <p>${t.intro}</p>

  <!-- ${t.todo} -->

`,
    }, lang)),

    // V3303 — CSS decides how it looks. A rule is: pick a thing, set a look.
    // Both checks are on computed style, so any valid answer passes.
    V3303: bilingual(COPY.V3303, (t, lang) => miniPage({
        style: `  #titulo { color: #2563EB; font-size: 20px; }

  .destacado {
    background-color: #ffffff;
    padding: 12px;
    border-radius: 8px;
  }

  /* ${t.todo} */
`,
        body: `  <h1 id="titulo">${t.title}</h1>

  <p class="destacado">${t.paragraph}</p>

`,
    }, lang)),

    // V3304 — JavaScript makes something happen. The listener is already wired;
    // the student only fills in what happens, so "a click runs an action" lands
    // without a word about functions or events.
    V3304: bilingual(COPY.V3304, (t, lang) => miniPage({
        style: `  button {
    background: #2563EB; color: #fff; border: 0;
    border-radius: 8px; padding: 10px 16px;
    font-size: 15px; font-weight: 700; cursor: pointer;
  }
  #mensaje { font-size: 20px; font-weight: 700; margin-top: 16px; }
`,
        body: `  <h1>${t.title}</h1>

  <button id="saludar">${t.button}</button>

  <p id="mensaje"></p>

`,
        script: `  // ${t.wiring}
  document.getElementById('saludar').addEventListener('click', function () {

    // ${t.todo}

  });
`,
    }, lang)),

    // V3306 — values and lists. They change data, never the mechanism: the
    // greeting comes from a variable and the menu from three sibling <li>s.
    V3306: bilingual(COPY.V3306, (t, lang) => miniPage({
        style: `  #saludo { color: #2563EB; font-size: 26px; margin: 4px 0 16px; }
  ul { padding-left: 20px; }
  li { padding: 4px 0; }
`,
        body: `  <p>${t.welcome}</p>
  <h1 id="saludo"></h1>

  <p>${t.dishesIntro}</p>

  <ul id="lista">
    <li>Tacos al pastor</li>
    <li>Quesadillas</li>
    <!-- ${t.todoList} -->
  </ul>

`,
        script: `  // ${t.valueNote}
  // ${t.todoValue}
  var ${t.nameVar} = "";

  document.getElementById('saludo').textContent = ${t.nameVar};
`,
    }, lang)),

    // V3307 — decisions. The whole if/else is written for them; they change the
    // number and watch which half runs. Reading and predicting, not authoring.
    // The `disponible`/`agotado` CLASS names are pinned (the checks read them);
    // the words shown on screen are not.
    V3307: bilingual(COPY.V3307, (t, lang) => miniPage({
        style: `  #estado {
    display: inline-block; padding: 6px 14px;
    border-radius: 999px; font-weight: 700; font-size: 14px;
  }
  .disponible { background: #DCFCE7; color: #16A34A; }
  .agotado { background: #FEE2E2; color: #DC2626; }
`,
        body: `  <h1>${t.title}</h1>

  <p id="estado"></p>

`,
        script: `  // ${t.todo}
  var ${t.quantityVar} = 5;

  var estado = document.getElementById('estado');

  // ${t.ifNote}
  if (${t.quantityVar} > 0) {
    estado.textContent = "${t.available}";
    estado.className = "disponible";
  } else {
    estado.textContent = "${t.soldOut}";
    estado.className = "agotado";
  }
`,
    }, lang)),

    // V3308 — the transfer lesson. One id is misspelled, so clicking throws and
    // the page does nothing. The point is not to spot it unaided: it's to run it,
    // read the error, and hand that error to Claude verbatim.
    // The typo (`resultdo` for `resultado`) is the lesson — keep it in both.
    V3308: bilingual(COPY.V3308, (t, lang) => miniPage({
        style: `  button {
    background: #2563EB; color: #fff; border: 0;
    border-radius: 8px; padding: 10px 16px;
    font-size: 15px; font-weight: 700; cursor: pointer;
  }
  #resultado { font-size: 20px; font-weight: 700; margin-top: 16px; }
`,
        body: `  <h1>${t.title}</h1>

  <p>${t.priceLabel} <span id="precio">120</span> ${t.currency}</p>

  <button id="calcular">${t.button}</button>

  <p id="resultado"></p>

`,
        script: `  document.getElementById('calcular').addEventListener('click', function () {
    var ${t.priceVar} = Number(document.getElementById('precio').textContent);
    var ${t.tipVar} = ${t.priceVar} * 0.15;

    // ${t.brokenNote}
    // ${t.todo}
    document.getElementById('resultdo').textContent = "${t.resultLabel}" + ${t.tipVar} + " ${t.currency}";
  });
`,
    }, lang)),

    // ── CD02 Module 3 ────────────────────────────────────────────────────────
    // V2307 — Functions, reframed as additive (see AUTHORED_TESTS above).
    // The first button works with its logic written inline; the student writes
    // the shared function once and wires the other two to it.
    V2307: bilingual(COPY.V2307, (t, lang) => shell(`    <div class="card">
      <h2>${t.heading}</h2>
      <p>${t.intro}</p>

      <button id="btnHola" class="btn" onclick="document.getElementById('salida').textContent = '${t.hello}'">${t.hello}</button>
      <button id="btnDias" class="btn">${t.morning}</button>
      <button id="btnBienvenido" class="btn">${t.welcome}</button>

      <p id="salida"></p>
    </div>
`, `  .btn { margin: 4px 4px 0 0; }
  #salida { margin-top: 16px; font-size: 20px; font-weight: 700; }
`, `  <script>
    // ${t.note}
    //
    // ${t.todo}
  </script>
`, lang)),

    // V2403 — Building your app's main screen.
    // They arrive with only the branded header from day one; the body is theirs.
    V2403: bilingual(COPY.V2403, (t, lang) => shell(`    <div class="card">
      <h2>${t.heading}</h2>
      <p>${t.intro}</p>
    </div>

    <!-- ${t.todo} -->
`, "", "", lang)),

    // V2404 — Making it interactive: buttons and forms.
    V2404: bilingual(COPY.V2404, (t, lang) => shell(`    <div class="card">
      <h2>${t.heading}</h2>
      <p>${t.intro}</p>

      <input id="entrada" type="text" placeholder="${t.placeholder}" />
      <button id="agregar" class="btn">${t.button}</button>

      <ul id="lista"></ul>
    </div>
`, "", `  <script>
    // ${t.todo}
  </script>
`, lang)),

    // V2405 — Saving your data so it's still there tomorrow.
    // Arrives with a working add button; now it has to survive a reload.
    // The `misDatos` storage KEY is pinned across locales — the checks read it.
    V2405: bilingual(COPY.V2405, (t, lang) => shell(`    <div class="card">
      <h2>${t.heading}</h2>
      <p>${t.intro}</p>

      <input id="entrada" type="text" placeholder="${t.placeholder}" />
      <button id="agregar" class="btn">${t.button}</button>

      <ul id="lista"></ul>
    </div>
`, "", `  <script>
    var lista = document.getElementById('lista');

    function ${t.addFn}(${t.textVar}) {
      var li = document.createElement('li');
      li.className = 'item';
      li.innerHTML = '<span class="dot"></span>' + ${t.textVar};
      lista.appendChild(li);
    }

    document.getElementById('agregar').addEventListener('click', function () {
      var ${t.textVar} = document.getElementById('entrada').value.trim();
      if (!${t.textVar}) return;
      ${t.addFn}(${t.textVar});
      document.getElementById('entrada').value = '';

      // ${t.todo}
    });
  </script>
`, lang)),

    // V2406 — Showing your saved data as a list (load on open).
    V2406: bilingual(COPY.V2406, (t, lang) => shell(`    <div class="card">
      <h2>${t.heading}</h2>
      <p>${t.intro}</p>

      <input id="entrada" type="text" placeholder="${t.placeholder}" />
      <button id="agregar" class="btn">${t.button}</button>

      <ul id="lista"></ul>
    </div>
`, "", `  <script>
    var lista = document.getElementById('lista');

    function ${t.paintFn}(${t.textVar}) {
      var li = document.createElement('li');
      li.className = 'item';
      li.innerHTML = '<span class="dot"></span>' + ${t.textVar};
      lista.appendChild(li);
    }

    function ${t.savedFn}() {
      try { return JSON.parse(localStorage.getItem('misDatos') || '[]'); }
      catch (e) { return []; }
    }

    document.getElementById('agregar').addEventListener('click', function () {
      var ${t.textVar} = document.getElementById('entrada').value.trim();
      if (!${t.textVar}) return;
      var ${t.dataVar} = ${t.savedFn}();
      ${t.dataVar}.push(${t.textVar});
      localStorage.setItem('misDatos', JSON.stringify(${t.dataVar}));
      ${t.paintFn}(${t.textVar});
      document.getElementById('entrada').value = '';
    });

    // ${t.todo}
  </script>
`, lang)),

    // V2407 — Adding an AI feature.
    // Deliberately a stub: an API key can never live in the browser. The lesson
    // says so plainly; the student builds the INTERFACE, and the real call is
    // wired up server-side in the Starter App Template repo.
    V2407: bilingual(COPY.V2407, (t, lang) => shell(`    <div class="card">
      <h2>${t.heading}</h2>
      <p>${t.intro}</p>

      <input id="pregunta" type="text" placeholder="${t.placeholder}" />
      <button id="preguntar" class="btn">${t.button}</button>

      <div id="respuesta"></div>
    </div>
`, `  #respuesta:not(:empty) {
    margin-top: 12px; padding: 12px 14px;
    background: #f6f7fb; border-left: 3px solid var(--accent);
    border-radius: 6px; font-size: 14px; line-height: 1.5;
  }
`, `  <script>
    // ${t.keyNote}
    function ${t.aiFn}(${t.aiArg}) {
      return '${t.stubIntro} ${t.echo}: "' + ${t.aiArg} + '"';
    }

    // ${t.todo}
  </script>
`, lang)),
};

/**
 * ══ CD02 MODULE 6 — THE CLICK-TO-IDENTIFY SCREENS ════════════════════════════
 *
 * The FINISHED replicas. Nothing is missing from them and there is nothing to type:
 * the student answers questions by clicking the part of the window each one
 * describes, and `hotspotHarness.js` turns every `[data-hs]` region into a target.
 *
 * WHY THESE ARE NOT WEB LAB LESSONS
 *
 * They used to be. The replicas were always right, but the exercise on top of them
 * was fill-in-the-blank HTML: to learn where the permission mode lives, the student
 * typed the word "Manual" between two tags. Three things were wrong with that.
 * It drilled a Module 3 skill ("text goes between the tags") to teach a Module 6
 * one. The answers were printed on screen next to every row, so the task was
 * transcription — the old hints admitted it: "both are written exactly as they
 * appear in the list above". And the instructions were addressed in `#ids`, to
 * students who have never opened a terminal.
 *
 * THE ONE RULE: ASK BY CONSEQUENCE, NEVER BY LABEL.
 *
 * "Click the fastest model" is worthless — the description beside the row says
 * "fast", so the student reads rather than knows. "A tiny rename, and you want it
 * cheap and instant" makes them map a SITUATION onto a control, which is the skill
 * the lesson claims to teach and the one they need the first time they open the real
 * app. Every prompt below is written that way. It is the difference between an
 * exercise and a copying task, and no test can check it — so it is checked here, in
 * review, exactly like the rule that `textContains` may only assert on product
 * names (see the note above AUTHORED_TESTS).
 *
 * AUTHORING A SCREEN
 *
 * Same chrome — `claudeCodeScreen` for the dark app window, `browserScreen` for
 * V2512's page on github.com — and the same `COPY` entry as before, minus the
 * exercise plumbing: no `.blank` spans, no `todo` comments, no output spans and no
 * `script`.
 * The region comments are gone too — they were the lesson when the student read the
 * source, and nobody reads the source now. What replaces them is `spots[].label`,
 * the chip that gets pinned to each region as it is found, so a finished screen ends
 * up annotated and worth coming back to.
 *
 * `data-hs` marks a target. Two constraints, both enforced by
 * authoredLessons.test.js: every id a target names must exist on that screen, and
 * NO `[data-hs]` may sit inside another one — the runner resolves a click with
 * `closest("[data-hs]")`, so a nested pair would make the inner one unclickable as
 * its parent. On V2605 that is why `.branch` and `.stat` are targets but the
 * `.projbar` around them is not.
 */
export const AUTHORED_SCREENS = {
    // V2501 — the version history panel (Module 5, Git).
    //
    // The commit rows are NOT targets; three of their children are. The runner
    // resolves a click with closest("[data-hs]"), so marking the row as well would
    // swallow every click meant for the hash, the message or Restore — and the
    // message question exists precisely to make the student tell those three apart.
    // The hash and the timestamp are deliberately present and deliberately NOT
    // targets: they are the wrong answers that make "which one tells you what
    // changed" a real question instead of the only thing on the row.
    V2501: bilingual(COPY.V2501, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="topbar">
    <span>${t.title}</span>
    <span class="branch" data-hs="branch">${t.branch}</span>
    <button class="pr" data-hs="push">${t.push}</button>
  </div>

  <div class="pad">

    <div class="h">${t.pendingHead}</div>
    <div class="row">
      <span class="badge" data-hs="pending">${t.pending}</span>
      <span class="stat"><span class="plus">${t.stat}</span></span>
    </div>

    <div class="h">${t.saveHead}</div>
    <div class="row">
      <span class="box">${t.msgPlaceholder}</span>
      <button class="btn yes" data-hs="commit">${t.commit}</button>
    </div>

    <div class="h">${t.historyHead}</div>
    <div class="menu">
      <div class="mrow on">
        <span class="mkey">${t.h3}</span>
        <span class="mdesc" data-hs="message">${t.d3}</span>
        <span class="dim">${t.t3}</span>
      </div>
      <div class="mrow">
        <span class="mkey">${t.h2}</span>
        <span class="mdesc">${t.d2}</span>
        <span class="dim">${t.t2}</span>
        <button class="pr" data-hs="restore">${t.restore}</button>
      </div>
      <div class="mrow">
        <span class="mkey">${t.h1}</span>
        <span class="mdesc">${t.d1}</span>
        <span class="dim">${t.t1}</span>
      </div>
    </div>

  </div>

</div>
`,
    }, lang)),

    // V4511 — CD04's version-history panel: V2501's screen with the "Push to GitHub"
    // button removed. See COPY.V4511 for why CD04 needs its own rather than aliasing.
    //
    // The topbar keeps the branch chip and loses only the button. Everything below the
    // topbar is identical to V2501, deliberately — the five local-history questions are
    // unchanged, and so are the wrong answers that make them real questions.
    V4511: bilingual(COPY.V4511, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="topbar">
    <span>${t.title}</span>
    <span class="branch" data-hs="branch">${t.branch}</span>
  </div>

  <div class="pad">

    <div class="h">${t.pendingHead}</div>
    <div class="row">
      <span class="badge" data-hs="pending">${t.pending}</span>
      <span class="stat"><span class="plus">${t.stat}</span></span>
    </div>

    <div class="h">${t.saveHead}</div>
    <div class="row">
      <span class="box">${t.msgPlaceholder}</span>
      <button class="btn yes" data-hs="commit">${t.commit}</button>
    </div>

    <div class="h">${t.historyHead}</div>
    <div class="menu">
      <div class="mrow on">
        <span class="mkey">${t.h3}</span>
        <span class="mdesc" data-hs="message">${t.d3}</span>
        <span class="dim">${t.t3}</span>
      </div>
      <div class="mrow">
        <span class="mkey">${t.h2}</span>
        <span class="mdesc">${t.d2}</span>
        <span class="dim">${t.t2}</span>
        <button class="pr" data-hs="restore">${t.restore}</button>
      </div>
      <div class="mrow">
        <span class="mkey">${t.h1}</span>
        <span class="mdesc">${t.d1}</span>
        <span class="dim">${t.t1}</span>
      </div>
    </div>

  </div>

</div>
`,
    }, lang)),

    // V2512 — a repository page (Module 5, GitHub). The only `browserScreen` in the
    // course; see the note on that helper for why this one is not the dark window.
    //
    // The last-commit line marks `.msg` and NOT the row: `who` and `when` sit beside
    // it as the wrong answers that make "which one tells you what changed" a real
    // question, exactly as V2501's hash and timestamp do. Same reason the file rows
    // are unmarked and only two `.fname` spans are targets — the row would swallow
    // clicks meant for the name.
    V2512: bilingual(COPY.V2512, (t, lang) => browserScreen({
        body: `<div class="win">

  <div class="chrome">
    <span class="dots">● ● ●</span>
    <span class="url" data-hs="url">${t.url}</span>
    <span class="avatar" data-hs="avatar">M</span>
  </div>

  <div class="page">

    <div class="repohead">
      <span class="owner">${t.owner}</span>
      <span class="reponame">${t.repo}</span>
      <span class="vis">${t.vis}</span>
    </div>

    <div class="navtabs">
      <button class="navtab on">${t.tabCode}</button>
      <button class="navtab" data-hs="issues">${t.tabIssues}</button>
      <button class="navtab" data-hs="settings">${t.tabSettings}</button>
    </div>

    <div class="toolbar">
      <span class="branch" data-hs="branch">${t.branch}</span>
      <span class="commits" data-hs="commits"><b>${t.commits}</b></span>
    </div>

    <div class="list">

      <div class="commitline">
        <span class="who">${t.who}</span>
        <span class="msg" data-hs="lastcommit">${t.msg}</span>
        <span class="when">${t.when}</span>
      </div>

      <div class="file">
        <span class="fname" data-hs="indexfile">${t.files[0].name}</span>
        <span class="fmsg">${t.files[0].msg}</span>
        <span class="fwhen">${t.files[0].when}</span>
      </div>
      <div class="file">
        <span class="fname">${t.files[1].name}</span>
        <span class="fmsg">${t.files[1].msg}</span>
        <span class="fwhen">${t.files[1].when}</span>
      </div>
      <div class="file">
        <span class="fname">${t.files[2].name}</span>
        <span class="fmsg">${t.files[2].msg}</span>
        <span class="fwhen">${t.files[2].when}</span>
      </div>
      <div class="file">
        <span class="fname">${t.files[3].name}</span>
        <span class="fmsg">${t.files[3].msg}</span>
        <span class="fwhen">${t.files[3].when}</span>
      </div>
      <div class="file">
        <span class="fname" data-hs="appjs">${t.files[4].name}</span>
        <span class="fmsg">${t.files[4].msg}</span>
        <span class="fwhen">${t.files[4].when}</span>
      </div>
      <div class="file">
        <span class="fname">${t.files[5].name}</span>
        <span class="fmsg">${t.files[5].msg}</span>
        <span class="fwhen">${t.files[5].when}</span>
      </div>

    </div>

    <div class="readme">
      <h2>${t.readmeH}</h2>
      <p>${t.readmeP}</p>
    </div>

  </div>

</div>
`,
    }, lang)),

    // V2603 — the tab bar. The three tab BUTTONS are the targets, not the rows of the
    // list below: pressing a tab is the actual gesture, and the list is what the
    // student consults while answering. The only screen here with `revealDesc` off
    // among the menu-shaped ones, for that reason — veiling a reference list the
    // targets do not contain would hide it forever.
    V2603: bilingual(COPY.V2603, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="tabbar">
    <button class="tabbtn" data-hs="chat">${t.tabChat}</button>
    <button class="tabbtn" data-hs="cowork">${t.tabCowork}</button>
    <button class="tabbtn on" data-hs="code">${t.tabCode}</button>
  </div>

  <div class="pad">

    <div class="h">${t.listHead}</div>
    <div class="menu">
      <div class="mrow"><span class="mkey">${t.tabChat}</span><span class="mdesc">${t.dChat}</span></div>
      <div class="mrow"><span class="mkey">${t.tabCowork}</span><span class="mdesc">${t.dCowork}</span></div>
      <div class="mrow on"><span class="mkey">${t.tabCode}</span><span class="mdesc">${t.dCode}</span></div>
    </div>

  </div>

</div>
`,
    }, lang)),

    // V2604 — the environment chooser and Select folder. The folder is shown already
    // chosen: the question is which environment reaches it, not what it is called.
    V2604: bilingual(COPY.V2604, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="topbar"><span>${t.title}</span></div>

  <div class="pad">

    <div class="h">${t.envHead}</div>
    <div class="menu">
      <div class="mrow on" data-hs="local"><span class="mkey">${t.envLocal}</span><span class="mdesc">${t.dLocal}</span></div>
      <div class="mrow" data-hs="cloud"><span class="mkey">${t.envCloud}</span><span class="mdesc">${t.dCloud}</span></div>
      <div class="mrow" data-hs="ssh"><span class="mkey">${t.envSsh}</span><span class="mdesc">${t.dSsh}</span></div>
      <div class="mrow" data-hs="wsl"><span class="mkey">${t.envWsl}</span><span class="mdesc">${t.dWsl}</span></div>
    </div>

    <div class="h">${t.folderHead}</div>
    <div class="row">
      <span class="badge">${t.folderShown}</span>
      <button class="btn yes" data-hs="select">${t.select}</button>
    </div>
    <div class="dim">${t.folderNote}</div>

  </div>

</div>
`,
    }, lang)),

    // V2605 — the whole session window. The anatomy lesson, and the only screen
    // here built on the two-column `.app`: six regions, each asked for by what you
    // would need it for rather than by its name.
    V2605: bilingual(COPY.V2605, (t, lang) => claudeCodeScreen({
        body: `<div class="app">

  <aside class="side">
    <div class="iconrow">
      <span>☰</span>
      <span>▤</span>
      <span>🔍</span>
      <span>${t.navBack}</span>
      <span>${t.navFwd}</span>
    </div>
    <div class="tabs">
      <span class="tab">${t.tabChat}</span>
      <span class="tab">${t.tabCowork}</span>
      <span class="tab on">${t.tabCode}</span>
    </div>
    <div class="side-item">${t.newSession}</div>
    <div class="side-item">${t.navArtifacts}</div>
    <div class="side-item">${t.navCustomize}</div>
    <div class="side-item">${t.navMore}</div>

    <div data-hs="sessions">
      <div class="side-h">${t.sessionsHead}</div>
      <div class="chat on">${t.sessionOn}</div>
      <div class="chat">${t.session2}</div>
      <div class="chat">${t.session3}</div>
    </div>

    <div class="me">${t.account}</div>
  </aside>

  <div class="main">

    <div class="topbar">
      <span>${t.title}</span>
      <span class="badge">{{APP_NAME}}</span>
    </div>

    <div class="thread" data-hs="thread">
      <div class="act">
        <div class="dim">${t.actLabel}</div>
        <div>${t.actRead}</div>
        <div>${t.actSearch}</div>
        <div>${t.actEdit}</div>
      </div>
    </div>

    <div class="composer">

      <div class="projbar">
        <span>{{APP_NAME}}</span>
        <span class="branch" data-hs="branch">${t.branch}</span>
        <span class="stat" data-hs="stat"><span class="plus">${t.statPlus}</span> <span class="minus">${t.statMinus}</span></span>
        <button class="pr">${t.pr}</button>
      </div>

      <div class="box" data-hs="box">${t.promptExample}</div>

      <div class="ctrls">
        <span class="pill mode" data-hs="mode">${t.mode}</span>
        <span class="pill">${t.addLabel}</span>
        <span class="pill">${t.micLabel}</span>
        <span class="pill grow">${t.modelName}</span>
        <span class="pill">${t.effortName}</span>
        <button class="btn yes">${t.send}</button>
        <button class="btn">${t.stop}</button>
      </div>

    </div>
  </div>

</div>
`,
    }, lang)),

    // V2606 — the model menu. `revealDesc`: each row's description states outright
    // which one is fastest and which is most capable, so they stay blurred until the
    // row is found. The shortcut badge is a target too — "you can change model
    // mid-session" is the half of this lesson a menu screenshot cannot show.
    V2606: bilingual(COPY.V2606, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="topbar">
    <span>${t.title}</span>
    <span class="badge" data-hs="shortcut">${t.shortcut}</span>
  </div>

  <div class="pad">

    <div class="h">${t.head}</div>
    <div class="menu">
      <div class="mrow" data-hs="haiku"><span class="mkey">${t.kHaiku}</span><span class="mdesc">${t.dHaiku}</span></div>
      <div class="mrow on" data-hs="sonnet"><span class="mkey">${t.kSonnet}</span><span class="mdesc">${t.dSonnet}</span></div>
      <div class="mrow" data-hs="opus"><span class="mkey">${t.kOpus}</span><span class="mdesc">${t.dOpus}</span></div>
      <div class="mrow" data-hs="fable"><span class="mkey">${t.kFable}</span><span class="mdesc">${t.dFable}</span></div>
    </div>

    <div class="dim">${t.ringNote}</div>

  </div>

</div>
`,
    }, lang)),

    // V2607 — the effort menu. `ultracode` is listed alongside the five model levels
    // exactly as it appears in the real menu, which is what makes "one of these is not
    // a model effort level" a question worth asking rather than a trick.
    V2607: bilingual(COPY.V2607, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="topbar">
    <span>${t.title}</span>
    <span class="badge">${t.shortcut}</span>
  </div>

  <div class="pad">

    <div class="h">${t.head}</div>
    <div class="menu">
      <div class="mrow" data-hs="low"><span class="mkey">${t.kLow}</span><span class="mdesc">${t.dLow}</span></div>
      <div class="mrow" data-hs="medium"><span class="mkey">${t.kMedium}</span><span class="mdesc">${t.dMedium}</span></div>
      <div class="mrow on" data-hs="high"><span class="mkey">${t.kHigh}</span><span class="mdesc">${t.dHigh}</span></div>
      <div class="mrow" data-hs="xhigh"><span class="mkey">${t.kXhigh}</span><span class="mdesc">${t.dXhigh}</span></div>
      <div class="mrow" data-hs="max"><span class="mkey">${t.kMax}</span><span class="mdesc">${t.dMax}</span></div>
      <div class="mrow" data-hs="ultra"><span class="mkey">${t.kUltra}</span><span class="mdesc">${t.dUltra}</span></div>
    </div>

    <div class="dim">${t.defaultNote}</div>

  </div>

</div>
`,
    }, lang)),

    // V2608 — the permission mode selector. The Shift+Tab warning is a TARGET, not
    // just a note: believing that shortcut works here is the single most common thing
    // a tutorial gets wrong about the desktop app, so the student has to point at the
    // correction themselves.
    V2608: bilingual(COPY.V2608, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="topbar">
    <span>${t.title}</span>
    <span class="badge">${t.shortcut}</span>
  </div>

  <div class="pad">

    <div class="h">${t.head}</div>
    <div class="menu">
      <div class="mrow on" data-hs="manual"><span class="mkey">${t.kManual}</span><span class="mdesc">${t.dManual}</span></div>
      <div class="mrow" data-hs="accept"><span class="mkey">${t.kAccept}</span><span class="mdesc">${t.dAccept}</span></div>
      <div class="mrow" data-hs="plan"><span class="mkey">${t.kPlan}</span><span class="mdesc">${t.dPlan}</span></div>
      <div class="mrow" data-hs="auto"><span class="mkey">${t.kAuto}</span><span class="mdesc">${t.dAuto}</span></div>
      <div class="mrow" data-hs="bypass"><span class="mkey">${t.kBypass}</span><span class="mdesc">${t.dBypass}</span></div>
    </div>

    <div class="warn" data-hs="toggle">${t.warn}</div>

    <div class="dim">${t.memoryNote}</div>

  </div>

</div>
`,
    }, lang)),

    // V2609 — the approval moment. The diff lines are HTML-escaped in COPY so the
    // browser PRINTS the tags instead of building them; written as real markup the
    // student would see a rendered list where the proposed change should be.
    V2609: bilingual(COPY.V2609, (t, lang) => claudeCodeScreen({
        body: `<div class="app col">

  <div class="topbar"><span>${t.title}</span></div>

  <div class="pad">

    <div class="ask">
      <div class="ask-t">${t.askTitle}</div>

      <div class="dim" data-hs="file">${t.fileHead}: ${t.diffPath}</div>
      <pre class="diff">${t.diffKeep}
<span class="add" data-hs="add">${t.diffAdd}</span></pre>

      <button class="btn yes">${t.yes}</button>
      <button class="btn" data-hs="reject">${t.no}</button>
    </div>

    <div class="dim">${t.asked}</div>
    <div class="dim">${t.twoQ}</div>

    <div class="row">
      <span class="stat" data-hs="stat"><span class="plus">${t.stat}</span></span>
      <span class="dim">${t.statNote}</span>
    </div>

  </div>

</div>
`,
    }, lang)),
};

/**
 * The questions, in the order they are asked, and what each answer is called.
 *
 * `targets[].id` must match a `data-hs` on that lesson's screen. `label` is the chip
 * pinned to the region once it is found; `miss` is optional and only worth writing
 * where one specific wrong answer is the predictable one (on V2605, clicking the
 * branch when asked how much has changed) — otherwise the layout falls back to a
 * generic nudge from the translation bundle.
 *
 * `revealDesc: true` starts the menu descriptions blurred and uncovers each as its
 * row is found. On for the screens whose descriptions state the answer in words; OFF
 * for V2605, where the region names ARE the answers and blurring them would only
 * make the window unreadable.
 */
export const AUTHORED_HOTSPOTS = {
    // V2501 — asked in the order a beginner meets the ideas: what am I about to
    // lose, how do I save it, how do I find it again, how do I get it back, then
    // the two that answer "what if". `revealDesc` is off: the commit messages ARE
    // the history, and veiling them would hide the thing the message question is
    // about.
    V2501: bilingual(COPY.V2501, (t) => ({
        targets: [
            { id: "pending", prompt: t.spots.pending.prompt, label: t.spots.pending.label, miss: t.spots.pending.miss },
            { id: "commit", prompt: t.spots.commit.prompt, label: t.spots.commit.label },
            { id: "message", prompt: t.spots.message.prompt, label: t.spots.message.label, miss: t.spots.message.miss },
            { id: "restore", prompt: t.spots.restore.prompt, label: t.spots.restore.label },
            { id: "branch", prompt: t.spots.branch.prompt, label: t.spots.branch.label },
            { id: "push", prompt: t.spots.push.prompt, label: t.spots.push.label },
        ],
    })),

    // V4511 — CD04's five. Same order and same questions as V2501 above, without the
    // sixth: "your computer is stolen tonight" has no answer in a course that never
    // pushes anywhere, and it was a GRADED target. What is left is entirely about local
    // history, which is what CD04 actually teaches. See COPY.V4511.
    V4511: bilingual(COPY.V4511, (t) => ({
        targets: [
            { id: "pending", prompt: t.spots.pending.prompt, label: t.spots.pending.label, miss: t.spots.pending.miss },
            { id: "commit", prompt: t.spots.commit.prompt, label: t.spots.commit.label },
            { id: "message", prompt: t.spots.message.prompt, label: t.spots.message.label, miss: t.spots.message.miss },
            { id: "restore", prompt: t.spots.restore.prompt, label: t.spots.restore.label },
            { id: "branch", prompt: t.spots.branch.prompt, label: t.spots.branch.label },
        ],
    })),

    // V2512 — asked in the order you meet a repo page: where am I, what is here,
    // what happened last, what happened before that, and the two that set up the
    // next lesson (the branch you will publish, the tab that publishes it).
    // `revealDesc` is off for V2501's reason — the commit messages ARE the content
    // the questions are about, and veiling them would hide the answer.
    V2512: bilingual(COPY.V2512, (t) => ({
        targets: [
            { id: "url", prompt: t.spots.url.prompt, label: t.spots.url.label, miss: t.spots.url.miss },
            { id: "indexfile", prompt: t.spots.indexfile.prompt, label: t.spots.indexfile.label, miss: t.spots.indexfile.miss },
            { id: "lastcommit", prompt: t.spots.lastcommit.prompt, label: t.spots.lastcommit.label, miss: t.spots.lastcommit.miss },
            { id: "commits", prompt: t.spots.commits.prompt, label: t.spots.commits.label, miss: t.spots.commits.miss },
            { id: "branch", prompt: t.spots.branch.prompt, label: t.spots.branch.label },
            { id: "settings", prompt: t.spots.settings.prompt, label: t.spots.settings.label, miss: t.spots.settings.miss },
        ],
    })),

    V2603: bilingual(COPY.V2603, (t) => ({
        targets: [
            { id: "code", prompt: t.spots.code.prompt, label: t.spots.code.label, miss: t.spots.code.miss },
            { id: "chat", prompt: t.spots.chat.prompt, label: t.spots.chat.label },
            { id: "cowork", prompt: t.spots.cowork.prompt, label: t.spots.cowork.label },
        ],
    })),

    V2604: bilingual(COPY.V2604, (t) => ({
        revealDesc: true,
        targets: [
            { id: "local", prompt: t.spots.local.prompt, label: t.spots.local.label, miss: t.spots.local.miss },
            { id: "select", prompt: t.spots.select.prompt, label: t.spots.select.label },
            { id: "cloud", prompt: t.spots.cloud.prompt, label: t.spots.cloud.label },
            { id: "ssh", prompt: t.spots.ssh.prompt, label: t.spots.ssh.label },
        ],
    })),

    V2605: bilingual(COPY.V2605, (t) => ({
        targets: [
            { id: "thread", prompt: t.spots.thread.prompt, label: t.spots.thread.label },
            { id: "box", prompt: t.spots.box.prompt, label: t.spots.box.label },
            { id: "sessions", prompt: t.spots.sessions.prompt, label: t.spots.sessions.label },
            { id: "branch", prompt: t.spots.branch.prompt, label: t.spots.branch.label },
            { id: "stat", prompt: t.spots.stat.prompt, label: t.spots.stat.label, miss: t.spots.stat.miss },
            { id: "mode", prompt: t.spots.mode.prompt, label: t.spots.mode.label },
        ],
    })),

    V2606: bilingual(COPY.V2606, (t) => ({
        revealDesc: true,
        targets: [
            { id: "fable", prompt: t.spots.fable.prompt, label: t.spots.fable.label, miss: t.spots.fable.miss },
            { id: "haiku", prompt: t.spots.haiku.prompt, label: t.spots.haiku.label },
            { id: "shortcut", prompt: t.spots.shortcut.prompt, label: t.spots.shortcut.label },
        ],
    })),

    V2607: bilingual(COPY.V2607, (t) => ({
        revealDesc: true,
        targets: [
            { id: "ultra", prompt: t.spots.ultra.prompt, label: t.spots.ultra.label, miss: t.spots.ultra.miss },
            { id: "low", prompt: t.spots.low.prompt, label: t.spots.low.label },
            { id: "max", prompt: t.spots.max.prompt, label: t.spots.max.label },
        ],
    })),

    V2608: bilingual(COPY.V2608, (t) => ({
        revealDesc: true,
        targets: [
            { id: "manual", prompt: t.spots.manual.prompt, label: t.spots.manual.label, miss: t.spots.manual.miss },
            { id: "plan", prompt: t.spots.plan.prompt, label: t.spots.plan.label },
            { id: "bypass", prompt: t.spots.bypass.prompt, label: t.spots.bypass.label },
            { id: "toggle", prompt: t.spots.toggle.prompt, label: t.spots.toggle.label },
        ],
    })),

    V2609: bilingual(COPY.V2609, (t) => ({
        targets: [
            { id: "file", prompt: t.spots.file.prompt, label: t.spots.file.label },
            { id: "add", prompt: t.spots.add.prompt, label: t.spots.add.label, miss: t.spots.add.miss },
            { id: "reject", prompt: t.spots.reject.prompt, label: t.spots.reject.label },
            { id: "stat", prompt: t.spots.stat.prompt, label: t.spots.stat.label },
        ],
    })),
};

/**
 * The exercise text and hint for each authored lesson.
 *
 * Overrides the generated `exercisePrompt` / `hint` on load, for the same reason
 * the scaffold and the checks do: the generator wrote its prose against a starter
 * the student never receives. Each one is written from this lesson's checks, so
 * every check description reads as a direct consequence of it.
 *
 * `lessonMarkdown` is deliberately NOT overridden — see the file header.
 */
export const AUTHORED_EXERCISES = Object.keys(COPY).reduce((acc, lessonID) => {
    const c = COPY[lessonID];
    acc[lessonID] = {
        es: { exercisePrompt: c.es.exercise, hint: c.es.hint },
        en: { exercisePrompt: c.en.exercise, hint: c.en.hint },
    };
    return acc;
}, {});

/**
 * Data pre-loaded into a lesson's shimmed localStorage the first time it opens.
 *
 * V2406 teaches "show what you already saved", which is untestable — and
 * pedagogically pointless — against an empty store. Per-lesson storage is
 * namespaced (`weblab:{lessonID}`), so data saved back in V2405 isn't visible
 * here; seeding gives the lesson something real to render.
 *
 * Not locale-keyed: the seeded values are dish names, which stay as they are in
 * both languages, so the `textContains: "Tacos"` check holds either way.
 */
export const AUTHORED_SEED_STORE = {
    V2406: { misDatos: JSON.stringify(["Tacos al pastor", "Quesadillas"]) },
};

/**
 * CD03's Module 4 is the same build chain as CD02's, under new lesson ids.
 *
 * Aliased rather than copied: a second set of five scaffolds and their matched
 * checks would be five more chances for the pair to drift apart, and a scaffold
 * whose checks point at elements it doesn't have is exactly the failure this file
 * was written to stop.
 *
 * What CD03 changes is the Module 3 that comes BEFORE this chain — its students
 * arrive able to read and place code rather than write it from scratch, so they
 * reach these TODOs by asking Claude and checking the result. The scaffolds and
 * assertions are indifferent to which way the code got there, so they carry over
 * untouched; only the generated prose differs, and that follows the lesson title.
 *
 * CD02's V2307 (functions) has no CD03 counterpart — that module no longer teaches
 * functions as a topic.
 */
const CD03_MODULE_4_ALIASES = {
    V3403: "V2403",
    V3404: "V2404",
    V3405: "V2405",
    V3406: "V2406",
    V3407: "V2407",
};

Object.entries(CD03_MODULE_4_ALIASES).forEach(([v3, v2]) => {
    AUTHORED_STARTERS[v3] = AUTHORED_STARTERS[v2];
    AUTHORED_TESTS[v3] = AUTHORED_TESTS[v2];
    AUTHORED_EXERCISES[v3] = AUTHORED_EXERCISES[v2];
    if (AUTHORED_SEED_STORE[v2]) AUTHORED_SEED_STORE[v3] = AUTHORED_SEED_STORE[v2];
});

/**
 * CD04 ("Claude AI Developer V4") is assembled from lessons chosen out of the three
 * earlier courses, so every authored asset it needs already exists under an older id.
 *
 * Aliased, never copied — for the reason the CD03 block above gives and one more:
 * a screen and its question set are a matched pair, and a hotspot whose targets
 * name ids its replica doesn't have is unanswerable rather than merely wrong. Two
 * copies of that pair is two chances to break it. The tests derive SCREEN_IDS and
 * LESSON_IDS from these maps, so the aliases inherit every parity assertion the
 * originals pass, and adding one to only SOME of the five maps fails at once.
 *
 * Module 3 (reading half): CD03's pages, minus its opening "first look" lesson,
 * which CD04 drops — Module 1 now covers what that lesson introduced in passing.
 * V4308 (functions) is CD02's V2307, the one Module 3 lesson CD02 authored.
 * V4305/V4306/V4307/V4309/V4310 are absent here on purpose: those lessons are
 * fully generated in CD02 too, so there is nothing to alias.
 *
 * Modules 4 and 5: the replica screens, all nine of them. Note V4405 ← V2611 and
 * V4406 ← V2605 — CD02 had to insert its "open your own project" lesson at the end
 * of the id range and read it out of order, whereas CD04 numbers it in place. Read
 * this map by the id on the LEFT; the right-hand column is not in sequence.
 */
const CD04_ALIASES = {
    // Module 3 — the reading pages, then the one authored writing lesson.
    V4301: "V3302",
    V4302: "V3303",
    V4303: "V3304",
    V4308: "V2307",

    // Module 4 — Claude Code. Every replica screen in the module.
    V4403: "V2603",
    V4404: "V2604",
    V4406: "V2605",
    V4407: "V2606",
    V4408: "V2607",
    V4409: "V2608",
    V4410: "V2609",

    // Module 5 — shipping. NOTHING is aliased here, and both absences are deliberate.
    //
    // CD02's V2512 GitHub repository page has no CD04 equivalent: CD04 deploys with
    // Firebase Hosting, so it has no lesson that reads a repo page. Where CD02 reads one
    // (V2512, `hotspot`), CD04 installs the CLI (V4513, `checklist`) — a terminal, which
    // is self-attested rather than rendered, so nothing needs a Firebase console screen.
    //
    // And V4511 no longer borrows CD02's Git screen: it has its OWN screen, questions and
    // copy above, because V2501's sixth question is graded on "Push to GitHub" and CD04
    // students never push. See COPY.V4511.
};

Object.entries(CD04_ALIASES).forEach(([v4, src]) => {
    if (AUTHORED_STARTERS[src]) AUTHORED_STARTERS[v4] = AUTHORED_STARTERS[src];
    if (AUTHORED_TESTS[src]) AUTHORED_TESTS[v4] = AUTHORED_TESTS[src];
    if (AUTHORED_EXERCISES[src]) AUTHORED_EXERCISES[v4] = AUTHORED_EXERCISES[src];
    if (AUTHORED_SCREENS[src]) AUTHORED_SCREENS[v4] = AUTHORED_SCREENS[src];
    if (AUTHORED_HOTSPOTS[src]) AUTHORED_HOTSPOTS[v4] = AUTHORED_HOTSPOTS[src];
    if (AUTHORED_SEED_STORE[src]) AUTHORED_SEED_STORE[v4] = AUTHORED_SEED_STORE[src];
});

/**
 * The three CD04 Module 3 lessons are shown as THREE FILES, not one document.
 *
 * The scaffold is still CD03's single page — `multiFile` on the registry entry makes
 * WebLabLayout split it into index.html / style.css / script.js on tabs, and join it
 * back before every run (components/PromptLabHub/webLabFiles.js). The scaffold, the
 * checks and the ids are all unaffected, so the aliases above still stand.
 *
 * The INSTRUCTIONS are not. V3303 says "make two changes inside <style>" and V3302's
 * hint says "not inside <style> or <script>" — and in the tabbed view there is no
 * <style> tag anywhere for the student to find. Both would send them hunting through
 * index.html for something that is now one tab over.
 *
 * Overridden here rather than edited in COPY, for the reason COPY.V4511 spells out at
 * length: V3302/V3303/V3304 still serve CD03, which is live and single-file, and there
 * the original wording is the correct wording. Only the two locales of the exercise and
 * the hint differ; everything else about these lessons stays shared.
 *
 * V4303 (JavaScript) had no tag reference to fix — its exercise already addressed the
 * addEventListener rather than the <script> around it — but it names script.js anyway,
 * so all three lessons say where to type in the same voice.
 */
Object.assign(AUTHORED_EXERCISES, {
    V4301: {
        es: {
            exercisePrompt: `Pídele a Claude "un párrafo y un botón en HTML" y pega lo que te dé en index.html, dentro de <body>, donde está el comentario TODO. La página debe terminar con un párrafo más de los que tiene ahora, y con un botón.`,
            hint: "El HTML va en la pestaña index.html, dentro de <body>. No lo pegues en style.css ni en script.js: ahí no funciona.",
        },
        en: {
            exercisePrompt: `Ask Claude for "a paragraph and a button in HTML" and paste what it gives you into index.html, inside <body>, where the TODO comment is. The page should end up with one more paragraph than it has now, plus a button.`,
            hint: "HTML goes in the index.html tab, inside <body>. Don't paste it into style.css or script.js — it won't work there.",
        },
    },
    V4302: {
        es: {
            exercisePrompt: `Abre la pestaña style.css y haz dos cambios: cambia el background-color de .destacado a cualquier otro color (por ejemplo #DBEAFE), y cambia el font-size de #titulo a un tamaño distinto de 20px.`,
            hint: "Las dos reglas ya existen en style.css. Solo tienes que cambiar el valor que va después de los dos puntos.",
        },
        en: {
            exercisePrompt: `Open the style.css tab and make two changes: change the background-color of .destacado to any other color (for example #DBEAFE), and change the font-size of #titulo to something other than 20px.`,
            hint: "Both rules are already in style.css. All you have to change is the value after the colon.",
        },
    },
    V4303: {
        es: {
            exercisePrompt: `Abre la pestaña script.js y, dentro del addEventListener, escribe una línea que ponga un mensaje en #mensaje. Después pulsa Ejecutar y toca el botón "Saludar".`,
            hint: "En script.js, usa document.getElementById('mensaje').textContent = 'tu mensaje';",
        },
        en: {
            exercisePrompt: `Open the script.js tab and, inside the addEventListener, write one line that puts a message into #mensaje. Then press Run and tap the "Say hello" button.`,
            hint: "In script.js, use document.getElementById('mensaje').textContent = 'your message';",
        },
    },
});

/** Resolve a locale-keyed authored entry, falling back to English then Spanish. */
const pickLocale = (entry, locale) => {
    if (!entry) return null;
    return entry[locale] || entry.en || entry.es || null;
};

/**
 * Substitute the student's own brand into an authored document.
 *
 * Shared by the Web Lab scaffolds and the Module 6 screens — V2605's replica shows
 * their project name in the title bar and the project bar, so it needs exactly the
 * same treatment as a scaffold.
 */
const fillBrand = (template, project, locale) => {
    const fallback = locale === "es"
        ? { appName: "Mi App", tagline: "Hecha por mí, con IA" }
        : { appName: "My App", tagline: "Made by me, with AI" };

    const values = {
        "{{APP_NAME}}": project?.appName || fallback.appName,
        "{{TAGLINE}}": project?.tagline || fallback.tagline,
        "{{ACCENT}}": project?.accentColor || DEFAULT_BRAND.accentColor,
        "{{EMOJI}}": project?.emoji || DEFAULT_BRAND.emoji,
    };

    // split/join rather than String.replace: it substitutes EVERY occurrence
    // ({{APP_NAME}} appears in both <title> and <h1>) and it treats the value as
    // literal text, so an app name containing `$&` can't be expanded into the
    // matched token.
    return Object.keys(values).reduce(
        (acc, token) => acc.split(token).join(values[token]),
        template
    );
};

/**
 * Fill an authored scaffold with the student's own brand.
 *
 * @param {string} lessonID  e.g. "V2403", "V3301"
 * @param {object} project   Their `users_projects` record (may be null)
 * @param {string} locale    "es" | "en"
 * @returns {string|null}    The starter document, or null if this lesson has no scaffold.
 */
export const buildAuthoredStarter = (lessonID, project, locale = "en") => {
    const template = pickLocale(AUTHORED_STARTERS[lessonID], locale);
    if (!template) return null;
    return fillBrand(template, project, locale);
};

/**
 * The finished replica screen for a Module 6 lesson, in the student's language.
 *
 * Takes `project` for the same reason `buildAuthoredStarter` does: the student's app
 * name appears on V2605's window. That is also why the screen is rendered in a
 * sandboxed iframe and never with dangerouslySetInnerHTML — the name is text the
 * student typed, so this document is not trustworthy markup even though we wrote the
 * frame around it. See hotspotHarness.js.
 *
 * @returns {string|null} null when this lesson has no authored screen.
 */
export const buildAuthoredScreen = (lessonID, project, locale = "en") => {
    const template = pickLocale(AUTHORED_SCREENS[lessonID], locale);
    if (!template) return null;
    return fillBrand(template, project, locale);
};

/**
 * The questions for a Module 6 screen, in the student's language.
 * @returns {{revealDesc?: boolean, targets: Array}|null}
 */
export const buildAuthoredHotspots = (lessonID, locale = "en") =>
    pickLocale(AUTHORED_HOTSPOTS[lessonID], locale);

/**
 * The checks for an authored lesson, in the student's language.
 * @returns {Array|null} null when this lesson has no authored checks.
 */
export const buildAuthoredTests = (lessonID, locale = "en") =>
    pickLocale(AUTHORED_TESTS[lessonID], locale);

/**
 * The exercise text and hint for an authored lesson, in the student's language.
 * @returns {{exercisePrompt: string, hint: string}|null}
 */
export const buildAuthoredExercise = (lessonID, locale = "en") =>
    pickLocale(AUTHORED_EXERCISES[lessonID], locale);

export default AUTHORED_STARTERS;
